import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'
import { createPortal } from 'react-dom'
import {
  formatCell,
  formatId,
  formatNumber,
  formatPercentPoints,
  formatResidual,
  isDatedStreamsColumn,
  isIdColumn,
  isLevelPercentColumn,
  isMultiweekPctChgColumn,
  isPercentColumn,
  isResidualColumn,
  pctChgColumnForStreamsWeek,
  percentTone,
  shortDatedStreamsHeader,
} from '../../utils/snoop/format'
import { peakHighlightTone } from '../../utils/snoop/peakHighlight'
import {
  engagementHighlightTone,
  ENGAGEMENT_TONE_TITLE,
} from '../../utils/snoop/engagementHighlight'
import {
  ARTIST_STATUS_OPTIONS,
  applyLuminateStatusCascade,
  compareUnsignedStatus,
  luminateStoredStatus,
  normalizeStatusOptions,
  statusDisplayLabel,
} from '../../utils/snoop/statusOptions'
import {
  DEFAULT_NEC_COLUMNS,
  rowHasNonEnglish,
} from '../../utils/snoop/nonEnglish'
import MultiSelectFilter from './MultiSelectFilter'
import {
  distinctColumnOptionsFromRows,
  genreStartsWithOptionsFromRows,
  rowMatchesExactColumn,
  rowMatchesGenreStartsWith,
} from '../../utils/snoop/luminateColumnFilters'
import {
  analyzeIdentityFromRow,
  analyzeLinkColumn,
  analyzePath,
} from '../../utils/snoop/analyzeLink'
import PubMappingEdit, { PUB_MAPPING_EDIT_COLUMN } from './PubMappingEdit'
import MappingPlatformEdit, {
  MAPPING_EDIT_COLUMN,
} from './MappingPlatformEdit'
import { Link } from 'react-router-dom'
import StreamsHistoryChart from './StreamsHistoryChart'

const PAGE_SIZE = 200

function isSortEmpty(value) {
  if (value === null || value === undefined) return true
  if (typeof value === 'number' && Number.isNaN(value)) return true
  const s = String(value).trim()
  if (!s) return true
  const lower = s.toLowerCase()
  return lower === 'nan' || lower === 'none' || lower === 'null'
}

/** Ascending compare only; callers handle empties-last + direction. */
function compareValues(a, b) {
  if (a === b) return 0

  const aNum = Number(a)
  const bNum = Number(b)
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
    return aNum - bNum
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
}

/** Chronological compare for Updated At / Created At (ISO or parseable). */
function compareTimestamps(a, b) {
  const aMs = Date.parse(a)
  const bMs = Date.parse(b)
  if (!Number.isNaN(aMs) && !Number.isNaN(bMs)) return aMs - bMs
  return compareValues(a, b)
}

/** Columns holding a short ``MM/DD/YY`` date (Assets "Folder Added"). */
const SHORT_DATE_COLUMNS = new Set(['Added'])

/**
 * Compare ``MM/DD/YY`` dates chronologically.
 *
 * Date.parse mis-reads a 2-digit year (``10/19/26`` -> 1926), and a plain
 * string compare puts December above January, so parse the parts directly.
 */
function compareShortDates(a, b) {
  const parse = (v) => {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(String(v ?? '').trim())
    if (!m) return null
    const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])
    return year * 10000 + Number(m[1]) * 100 + Number(m[2])
  }
  const av = parse(a)
  const bv = parse(b)
  if (av != null && bv != null) return av - bv
  return compareValues(a, b)
}

/** Empty/null always sinks to the bottom, for both asc and desc. */
function compareSortable(a, b, direction, compareFn) {
  const aEmpty = isSortEmpty(a)
  const bEmpty = isSortEmpty(b)
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  const cmp = compareFn(a, b)
  return direction === 'asc' ? cmp : -cmp
}

/** Cap stacked header sorts (primary + secondaries). */
const MAX_SORT_SPECS = 3

/** Normalize defaultSort / state to ``[{ column, direction }, ...]``. */
function normalizeSortSpecs(sort) {
  if (!sort) return []
  const list = Array.isArray(sort) ? sort : sort?.column ? [sort] : []
  return list
    .filter((s) => s && s.column)
    .map((s) => ({
      column: s.column,
      direction: s.direction === 'asc' ? 'asc' : 'desc',
    }))
}

function SortIndicator({ active, direction, rank = null, multi = false }) {
  if (!active) return <span className="text-gray-300">↕</span>
  return (
    <span className="inline-flex items-center text-[var(--encore-blue)]">
      <span>{direction === 'asc' ? '↑' : '↓'}</span>
      {multi && rank != null ? (
        <sup className="ml-px text-[9px] font-semibold leading-none">{rank}</sup>
      ) : null}
    </span>
  )
}

function statusSelectClass(value) {
  // Snoop dash merges both statuses into one namespaced control (`artist:Roster`).
  const lower = String(value || '')
    .toLowerCase()
    .replace(/^(snoop|artist):/, '')
  if (lower === 'priority') return 'border-sky-200 bg-sky-50 text-sky-800'
  // Use white bg so Archive does not look "stuck greying" on striped rows
  if (lower === 'archive') return 'border-gray-300 bg-white text-gray-800'
  if (lower === 'roster' || lower === 'distro') return 'border-indigo-200 bg-indigo-50 text-indigo-800'
  if (lower === 'cm') return 'border-violet-200 bg-violet-50 text-violet-800'
  // PGP pipeline stages, warm→cool as a deal progresses (Priority reuses sky above).
  if (lower === 'longform') return 'border-amber-200 bg-amber-50 text-amber-800'
  if (lower === 'proposal') return 'border-teal-200 bg-teal-50 text-teal-800'
  if (lower === 'watching') return 'border-slate-200 bg-slate-50 text-slate-700'
  if (lower === 'false credit') return 'border-rose-200 bg-rose-50 text-rose-800'
  if (lower === 'destroy' || lower === 'junk') return 'border-rose-200 bg-rose-50 text-rose-800'
  if (lower === 'core') return 'border-emerald-200 bg-emerald-50 text-emerald-800'
  if (lower === 'signed') return 'border-indigo-200 bg-indigo-50 text-indigo-800'
  if (lower === 'a&r') return 'border-sky-200 bg-sky-50 text-sky-800'
  if (lower === 'no status') return 'border-slate-200 bg-slate-50 text-slate-700'
  if (lower === 'private' || lower === 'burner') return 'border-amber-200 bg-amber-50 text-amber-800'
  if (lower.startsWith('signed')) return 'border-indigo-200 bg-indigo-50 text-indigo-800'
  if (lower.startsWith('unsigned')) return 'border-emerald-200 bg-emerald-50 text-emerald-800'
  return 'border-gray-200 bg-white text-gray-800'
}

/** Inline boolean control for flag columns (PGP Action = ball in our court). */
function BoolCheckbox({ checked, saving, onChange, title = null }) {
  return (
    <label
      className={[
        'inline-flex items-center justify-center',
        saving ? 'animate-pulse opacity-70' : '',
      ].join(' ')}
      title={
        saving
          ? 'Saving…'
          : title ||
            (checked ? 'Action item (click to clear)' : 'Mark as action item')
      }
    >
      <input
        type="checkbox"
        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-amber-600 focus:ring-amber-200 disabled:cursor-not-allowed"
        checked={Boolean(checked)}
        disabled={saving}
        onChange={(event) => onChange(event.target.checked)}
        aria-busy={saving || undefined}
      />
    </label>
  )
}

function statusSaveKey(column, uid, rowId = null) {
  if (rowId != null && String(rowId).trim() !== '' && String(rowId).toLowerCase() !== 'nan') {
    return `${column}:id:${String(rowId).trim().replace(/\.0$/, '')}`
  }
  const id = String(uid ?? '')
    .trim()
    .replace(/\.0$/, '')
  return `${column}:${id}`
}

function StatusSelect({
  value,
  options,
  disabled,
  saving,
  onChange,
  wide = false,
  allowBlank = true,
  centerText = false,
}) {
  const current = value == null || value === '' ? '' : String(value)
  const normalized = normalizeStatusOptions(options)
  const knownValues = new Set(normalized.map((opt) => opt.value))
  // Keep a blank option when the row is already blank so the control can display;
  // hide it once a real status is set if blank is not allowed.
  const showBlankOption = allowBlank || !current
  return (
    <select
      value={current}
      disabled={disabled || saving}
      onChange={(event) => onChange(event.target.value)}
      aria-busy={saving || undefined}
      className={[
        wide ? 'w-full min-w-[9.5rem] max-w-[14rem]' : 'w-[7.25rem]',
        'rounded-full border px-2 py-1 text-xs font-medium outline-none',
        // text-center also centers the closed control's selected value.
        centerText ? 'text-center' : '',
        'focus:ring-2 focus:ring-blue-100 disabled:opacity-60',
        statusSelectClass(current),
        saving ? 'animate-pulse' : '',
      ].join(' ')}
      title={saving ? 'Saving status…' : 'Updates status in Postgres'}
    >
      {showBlankOption ? <option value="">—</option> : null}
      {normalized.map((opt) => (
        // Display-only options exist so a legacy value still renders a label.
        // Keep them out of the list unless the row already holds that value,
        // which would otherwise leave the closed control with nothing to show.
        <option
          key={opt.value}
          value={opt.value}
          disabled={opt.disabled}
          hidden={opt.disabled && opt.value !== current}
        >
          {opt.label}
        </option>
      ))}
      {current && !knownValues.has(current) ? (
        <option value={current}>{statusDisplayLabel(current, options)}</option>
      ) : null}
    </select>
  )
}

function PercentPill({ value, formatted, highlightTone = null, signedTone = true }) {
  if (highlightTone === 'yellow' || highlightTone === 'green') {
    const bg = highlightTone === 'yellow' ? '#FFD700' : '#90EE90'
    return (
      <span
        className="inline-flex rounded px-1.5 py-0.5 text-xs font-semibold text-gray-950"
        style={{ backgroundColor: bg }}
        title={ENGAGEMENT_TONE_TITLE[highlightTone]}
      >
        {formatted}
      </span>
    )
  }
  const tone = signedTone ? percentTone(value) : 'neutral'
  const classes =
    tone === 'positive'
      ? 'bg-[var(--encore-green-soft)] text-[var(--encore-green)]'
      : tone === 'negative'
        ? 'bg-[var(--encore-red-soft)] text-[var(--encore-red)]'
        : 'bg-gray-100 text-gray-600'
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${classes}`}>
      {formatted}
    </span>
  )
}

/** Compact streams + WoW % stacked under one week column (Artists multiweek). */
function StreamsWithPctChgCell({ streamsValue, pctValue, pctColumn }) {
  const streamsFormatted =
    streamsValue === null || streamsValue === undefined || streamsValue === ''
      ? null
      : formatNumber(streamsValue)
  const pctFormatted = formatPercentPoints(pctValue, { signed: true, maxDecimals: 1 })
  const tone = percentTone(pctValue)
  const pctClass =
    pctFormatted === '—'
      ? 'text-gray-400'
      : tone === 'positive'
        ? 'text-[var(--encore-green)]'
        : tone === 'negative'
          ? 'text-[var(--encore-red)]'
          : 'text-gray-500'
  return (
    <div className="flex flex-col items-end gap-0.5 leading-none" title={pctColumn || undefined}>
      {streamsFormatted == null ? (
        <span className="text-gray-400">—</span>
      ) : (
        <span className="tabular-nums text-sm text-gray-800">{streamsFormatted}</span>
      )}
      <span className={`tabular-nums text-[10px] font-semibold ${pctClass}`}>{pctFormatted}</span>
    </div>
  )
}

function MetricNumber({ value, highlightTone = null }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400">—</span>
  }
  const formatted = formatNumber(value)
  if (highlightTone === 'yellow' || highlightTone === 'green') {
    const bg = highlightTone === 'yellow' ? '#FFD700' : '#90EE90'
    return (
      <span
        className="inline-flex rounded px-1.5 py-0.5 font-semibold text-gray-950 tabular-nums"
        style={{ backgroundColor: bg }}
        title={ENGAGEMENT_TONE_TITLE[highlightTone]}
      >
        {formatted}
      </span>
    )
  }
  return <span className="tabular-nums text-gray-800">{formatted}</span>
}

function GrowthNumber({ value, peakTone = null }) {
  const n = Number(value)
  if (value === null || value === undefined || value === '' || Number.isNaN(n)) {
    return <span className="text-gray-400">—</span>
  }
  const formatted = formatNumber(n)
  const signed = n > 0 ? `+${formatted}` : formatted

  if (peakTone === 'alltime') {
    return (
      <span
        className="inline-flex rounded px-1.5 py-0.5 font-semibold text-amber-950"
        style={{ backgroundColor: '#FFD700' }}
        title="Equals all-time daily growth peak"
      >
        {signed}
      </span>
    )
  }
  if (peakTone === '90d') {
    return (
      <span
        className="inline-flex rounded px-1.5 py-0.5 font-semibold text-emerald-950"
        style={{ backgroundColor: '#90EE90' }}
        title="Equals 90-day daily growth peak"
      >
        {signed}
      </span>
    )
  }

  const className =
    n > 0
      ? 'font-medium text-[var(--encore-green)]'
      : n < 0
        ? 'font-medium text-[var(--encore-red)]'
        : 'text-gray-800'
  return <span className={className}>{signed}</span>
}

function NewFollowsCell({ value }) {
  const n = Number(value)
  if (value === null || value === undefined || value === '' || Number.isNaN(n)) {
    return <span className="text-gray-400">—</span>
  }
  const formatted = formatNumber(n)
  if (n < 0) {
    return (
      <span className="inline-flex min-w-[1.5rem] justify-center rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums bg-rose-50 text-rose-700">
        {formatted}
      </span>
    )
  }
  if (n === 0) {
    return <span className="tabular-nums text-gray-500">{formatted}</span>
  }
  // Light green at 1 → darker as New Follows rises
  let classes = 'bg-emerald-50 text-emerald-700'
  if (n >= 8) classes = 'bg-emerald-500 text-white'
  else if (n >= 5) classes = 'bg-emerald-400 text-emerald-950'
  else if (n >= 3) classes = 'bg-emerald-300 text-emerald-950'
  else if (n === 2) classes = 'bg-emerald-200 text-emerald-900'
  return (
    <span className={`inline-flex min-w-[1.5rem] justify-center rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums ${classes}`}>
      {formatted}
    </span>
  )
}

const COUNT_COLUMNS = new Set(['Recent Count', 'Prior Count', 'New Follows'])
/** Compact "…" cells with full value on hover (long opaque IDs). */
const ELLIPSIS_ONLY_COLUMNS = new Set([
  'Luminate Song ID',
  'Luminate Artist ID',
  'Chartmetric ID',
  'YouTube',
  'Spotify',
  'Apple Music',
  'Dup Keys',
])

function positionHoverTip(anchorEl, { maxWidth = 420 } = {}) {
  const rect = anchorEl.getBoundingClientRect()
  const width = Math.min(maxWidth, window.innerWidth - 24)
  let left = rect.left
  if (left + width > window.innerWidth - 12) {
    left = Math.max(12, window.innerWidth - width - 12)
  }
  let top = rect.bottom + 6
  if (top + 120 > window.innerHeight - 12) {
    top = Math.max(12, rect.top - 12)
  }
  return { left, top, maxWidth: width }
}

/** True when CSS truncate is hiding part of the text (subpixel slack). */
function isTextOverflowing(el) {
  if (!el) return false
  return el.scrollWidth > el.clientWidth + 1
}

/** Truncated cell with a selectable hover popover (copy-friendly). */
function TruncatedText({ value, className = '', label = 'Full value', ellipsisOnly = false }) {
  const [tip, setTip] = useState(null)
  const hideTimer = useRef(null)

  if (
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'string' &&
      ['nan', 'none', 'null', '<na>'].includes(value.trim().toLowerCase()))
  ) {
    return <span className="text-gray-400">—</span>
  }
  const text = String(value)

  function clearHide() {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current)
      hideTimer.current = null
    }
  }

  function showTip(event) {
    const el = event.currentTarget
    // Ellipsis-only cells (Luminate Song/Artist ID, Spotify, …) always tip — the
    // full value is never shown in the cell, only "…".
    if (ellipsisOnly) {
      clearHide()
      setTip({ ...positionHoverTip(el), text, label })
      return
    }
    // Normal truncate: tip only when CSS is actually clipping the text.
    if (!isTextOverflowing(el)) {
      setTip(null)
      return
    }
    clearHide()
    setTip({ ...positionHoverTip(el), text, label })
  }

  function scheduleHide() {
    clearHide()
    hideTimer.current = setTimeout(() => setTip(null), 180)
  }

  return (
    <>
      <span
        className={[
          'block min-w-0 cursor-default',
          ellipsisOnly ? 'text-center tracking-widest text-gray-400' : 'truncate',
          className || 'text-gray-700',
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseEnter={showTip}
        onMouseLeave={scheduleHide}
      >
        {ellipsisOnly ? '…' : text}
      </span>
      {tip
        ? createPortal(
            <span
              role="tooltip"
              className="fixed z-[9999] select-text rounded-md bg-gray-900 px-2.5 py-2 text-[11px] leading-relaxed text-white shadow-lg"
              style={{ left: tip.left, top: tip.top, maxWidth: tip.maxWidth }}
              onMouseEnter={clearHide}
              onMouseLeave={scheduleHide}
            >
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-300">
                {tip.label}
                <span className="ml-1.5 font-normal normal-case tracking-normal text-gray-400">
                  select to copy
                </span>
              </span>
              <span className="block whitespace-normal break-all">{tip.text}</span>
            </span>,
            document.body,
          )
        : null}
    </>
  )
}

const NEW_FOLLOWS_USERNAMES_COL = 'New Follows Usernames'
const ALL_FOLLOWS_USERNAMES_COL = 'All Follows Usernames'
const SOURCE_USERNAME_COLUMNS = [NEW_FOLLOWS_USERNAMES_COL, ALL_FOLLOWS_USERNAMES_COL]

/** Split comma-separated source-username lists into trimmed handles. */
function parseSourceUsernames(value) {
  if (value == null || value === '') return []
  return String(value)
    .split(',')
    .map((part) => part.trim().replace(/^@/, ''))
    .filter((part) => part && part.toLowerCase() !== 'nan' && part.toLowerCase() !== 'none')
}

function collectSourceUsernameOptions(rows, column) {
  const seen = new Map()
  for (const row of rows) {
    for (const name of parseSourceUsernames(row[column])) {
      const key = name.toLowerCase()
      if (!seen.has(key)) seen.set(key, name)
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
}

function rowMatchesSourceFilter(row, column, selected, matchMode) {
  if (!selected.length) return true
  const wanted = selected.map((n) => n.toLowerCase())
  const present = new Set(parseSourceUsernames(row[column]).map((n) => n.toLowerCase()))
  return matchMode === 'and'
    ? wanted.every((name) => present.has(name))
    : wanted.some((name) => present.has(name))
}

function SourceUsernamesCell({ value }) {
  const [tip, setTip] = useState(null)
  const hideTimer = useRef(null)
  const names = parseSourceUsernames(value)
  if (!names.length) {
    return <span className="text-gray-400">—</span>
  }
  const text = names.join(', ')

  function clearHide() {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current)
      hideTimer.current = null
    }
  }

  function showTip(event) {
    const el = event.currentTarget
    if (!isTextOverflowing(el)) {
      setTip(null)
      return
    }
    clearHide()
    setTip({
      ...positionHoverTip(el),
      text,
      count: names.length,
    })
  }

  function scheduleHide() {
    clearHide()
    hideTimer.current = setTimeout(() => setTip(null), 180)
  }

  return (
    <>
      <span
        className="block min-w-0 cursor-default truncate text-gray-700"
        onMouseEnter={showTip}
        onMouseLeave={scheduleHide}
      >
        {text}
      </span>
      {tip
        ? createPortal(
            <span
              role="tooltip"
              className="fixed z-[9999] select-text rounded-md bg-gray-900 px-2.5 py-2 text-[11px] leading-relaxed text-white shadow-lg"
              style={{ left: tip.left, top: tip.top, maxWidth: tip.maxWidth }}
              onMouseEnter={clearHide}
              onMouseLeave={scheduleHide}
            >
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-300">
                {tip.count} source{tip.count === 1 ? '' : 's'}
                <span className="ml-1.5 font-normal normal-case tracking-normal text-gray-400">
                  select to copy
                </span>
              </span>
              <span className="block whitespace-normal break-words">{tip.text}</span>
            </span>,
            document.body,
          )
        : null}
    </>
  )
}

function TableSearchInput({
  placeholder = 'Search…',
  onQueryChange,
  className =
    'w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[var(--encore-blue)] focus:ring-2 focus:ring-blue-100 sm:max-w-md',
  debounceMs = 150,
}) {
  // Local state only — typing must not re-render the heavy table body.
  const [text, setText] = useState('')
  const onQueryChangeRef = useRef(onQueryChange)
  onQueryChangeRef.current = onQueryChange

  useEffect(() => {
    const id = window.setTimeout(() => {
      onQueryChangeRef.current?.(text)
    }, debounceMs)
    return () => window.clearTimeout(id)
  }, [text, debounceMs])

  return (
    <div className="relative inline-flex w-full sm:max-w-md">
      <input
        type="search"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={placeholder}
        className={[className, text ? 'pr-14' : ''].filter(Boolean).join(' ')}
        autoComplete="off"
        spellCheck={false}
      />
      {text ? (
        <button
          type="button"
          onClick={() => setText('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-1.5 py-0.5 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          title="Clear search"
          aria-label="Clear search"
        >
          Clear
        </button>
      ) : null}
    </div>
  )
}


const HIDDEN_UID_COLUMNS = ['IG User ID', 'Instagram UID', 'TikTok UID', 'Verified', 'id']
const HIDDEN_SNOOP_COLUMNS = ['Prior Count']

/** Username column → UID column(s) for hover tooltips (first hit wins). */
const USERNAME_UID_COLUMN = {
  'IG Username': ['IG User ID', 'Instagram UID'],
  Instagram: ['Instagram UID', 'IG User ID'],
  TikTok: ['TikTok UID'],
}

function isVerifiedValue(value) {
  if (value === true || value === 1) return true
  if (value === false || value === 0 || value == null || value === '') return false
  const s = String(value).trim().toLowerCase()
  return s === 'true' || s === '1' || s === 'yes' || s === 'verified'
}

function VerifiedBadge() {
  return (
    <svg
      className="inline-block h-3.5 w-3.5 shrink-0 text-[#0095f6]"
      viewBox="0 0 24 24"
      aria-label="Verified"
    >
      <title>Verified</title>
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        d="M10.1 15.6l-3.2-3.2 1.15-1.15 2.05 2.05 5.05-5.05 1.15 1.15-6.2 6.2z"
        fill="white"
      />
    </svg>
  )
}

function UsernameWithUid({ value, uid, verified = false }) {
  const [tip, setTip] = useState(null)

  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400">—</span>
  }
  const text = String(value)
  const uidText =
    uid != null && String(uid).trim() !== '' && String(uid).toLowerCase() !== 'nan'
      ? String(uid)
      : null

  function showTip(event) {
    const rect = event.currentTarget.getBoundingClientRect()
    setTip({
      left: rect.left,
      top: rect.top - 8,
      username: text,
      uid: uidText,
    })
  }

  return (
    <>
      <span
        className="inline-flex max-w-full min-w-0 items-center gap-1"
        onMouseEnter={showTip}
        onMouseLeave={() => setTip(null)}
      >
        <span className="truncate">{text}</span>
        {verified ? <VerifiedBadge /> : null}
      </span>
      {tip
        ? createPortal(
            <span
              role="tooltip"
              className="pointer-events-none fixed z-[9999] max-w-[min(20rem,calc(100vw-1.5rem))] -translate-y-full rounded-md bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-lg"
              style={{ left: tip.left, top: tip.top }}
            >
              {tip.uid ? (
                <span className="block font-mono whitespace-nowrap">UID: {tip.uid}</span>
              ) : null}
              <span
                className={[
                  'block whitespace-normal break-words',
                  tip.uid ? 'mt-0.5 text-gray-200' : '',
                ].join(' ')}
              >
                {tip.username}
              </span>
            </span>,
            document.body,
          )
        : null}
    </>
  )
}

/** Compact two-line headers for long metric names (full name in title). */
const SHORT_HEADERS = {
  // Operations -> Assets / Creative: narrow facet columns.
  Added: ['Folder', 'Added'],
  'Side Artist': ['Side', 'Artist'],
  'Cover Art': ['Cover', 'Art'],
  'Daily IG Follower Growth': ['Daily', 'Growth'],
  'Daily IG Growth %': ['Daily', '%'],
  'Weekly IG Follower Growth': ['Weekly', 'Growth'],
  'Weekly IG Growth %': ['Weekly', '%'],
  'Monthly IG Follower Growth': ['Monthly', 'Growth'],
  'Monthly IG Growth %': ['Monthly', '%'],
  'Daily TT Follower Growth': ['Daily', 'Growth'],
  'Daily TT Growth %': ['Daily', '%'],
  'Weekly TT Follower Growth': ['Weekly', 'Growth'],
  'Weekly TT Growth %': ['Weekly', '%'],
  'Monthly TT Follower Growth': ['Monthly', 'Growth'],
  'Monthly TT Growth %': ['Monthly', '%'],
  'IG Follower Count': ['Followers'],
  'TT Follower Count': ['Followers'],
  'Recent Count': ['Total', 'Follows'],
  'Prior Count': ['Prev'],
  'New Follows': ['New', 'Follows'],
  'New Follows Usernames': ['New Follows', 'Usernames'],
  'All Follows Usernames': ['All Follows', 'Usernames'],
  'IG Full Name': ['IG Full Name'],
  'Artist Status': ['Artist', 'Status'],
  '30D # IG Posts (max 12)': ['30D', 'Posts'],
  '30D Average IG Reels Engagement Rate (max 12)': ['Reels', 'Eng %'],
  '30D Median IG Reels Views (max 12)': ['Reels', 'Med Views'],
  '30D Median IG Non-Reels Engagement Rate (max 12)': ['Non-Reels', 'Eng %'],
  '30D # TT Posts (max 20)': ['30D', 'Posts'],
  '30D Median TT Views (max 20)': ['30D', 'Med Views'],
  '30D Average TT Engagement Rate (max 20)': ['30D', 'Eng %'],
  '30D Average TT Saves Rate (max 20)': ['30D', 'Saves %'],
  Residual: ['Residual'],
  'Chartmetric ID': ['CM'],
  'YT Search Topic ID': ['YT', 'Topic'],
  YouTube: ['YT'],
  Spotify: ['Spotify'],
  'Apple Music': ['Apple'],
  'Max Residual': ['Max', 'Residual'],
  'Max Over-index %': ['Max', 'OI %'],
  'Total Streams TW': ['TW'],
  'Total Streams LW': ['LW'],
  'Total Streams ATD': ['ATD'],
  'Track Count': ['Tracks'],
  'Primary Artist': ['Primary', 'Artist'],
  'Weeks Since Latest Release': ['Weeks', 'Latest'],
  'Weeks since release': ['Weeks'],
  'Audio % TW': ['Audio %'],
  'Luminate Genres': ['Genres'],
  'Luminate Song ID': ['ID'],
  'Luminate Artist ID': ['ID'],
  'Track Title': ['Track'],
  'Display Artist': ['Artist'],
  Imprint: ['Imprint'],
  'Country of Origin': ['Country'],
  Language: ['Lang'],
  'Artist Type': ['Type'],
  'In Track File': ['In', 'Tracks'],
  'Focus Country-Lang': ['Focus', 'C/L'],
  'Imprint DK/Self': ['DK/Self'],
  '# Weeks Up': ['Weeks Up'],
  Gender: ['Gender'],
  Score: ['Score'],
  'Streams TW': ['TW'],
  'Streams LW': ['LW'],
  'Streams ATD': ['ATD'],
  '% Chg': ['% Chg'],
}

/** Headers that should use medium weight (match non-bold Luminate metric look). */
const MEDIUM_WEIGHT_HEADERS = new Set([
  'Streams TW',
  'Streams LW',
  'Streams ATD',
  'Total Streams TW',
  'Total Streams LW',
  'Total Streams ATD',
  'Max Residual',
  'Max Over-index %',
  'Country of Origin',
  'Language',
  'Luminate Genres',
  'Luminate Artist ID',
  'Luminate Song ID',
  'Artist Type',
  'In Track File',
  '# Weeks Up',
  'Gender',
  'Score',
  'Imprint',
  'Name',
  'Track Title',
  'Display Artist',
  'Audio % TW',
  'Weeks since release',
  '% Chg',
  'Residual',
])

function isTruthyFlag(value) {
  if (value === true || value === 1) return true
  if (value === false || value === 0 || value == null || value === '') return false
  const s = String(value).trim().toLowerCase()
  return s === 'true' || s === '1' || s === 'yes' || s === 't'
}

/** Bold discovery-relevant cells from hidden Focus Country-Lang / Imprint DK/Self flags. */
function discoveryEmphasisClass(column, row) {
  if (!row) return ''
  if (column === 'Imprint' && isTruthyFlag(row['Imprint DK/Self'])) {
    return 'font-bold text-gray-900'
  }
  if (
    (column === 'Country of Origin' || column === 'Language') &&
    isTruthyFlag(row['Focus Country-Lang'])
  ) {
    return 'font-bold text-gray-900'
  }
  return ''
}
function columnHeaderLabel(column, { stackPctChgUnderStreams = false, headerLabels = null } = {}) {
  // Per-page header rename (PGP: Name → Artist, Lead A&R → Lead). Checked first
  // so an explicit label always wins over the built-in short/ellipsis headers.
  const override = headerLabels?.[column]
  if (override) {
    return (
      <span className="font-semibold uppercase tracking-wide" title={column}>
        {override}
      </span>
    )
  }
  if (USERNAME_UID_COLUMN[column]) {
    return (
      <span className="flex min-w-0 flex-col items-start gap-0.5 normal-case tracking-normal">
        <span className="font-semibold uppercase tracking-wide">{column}</span>
        <span className="text-[10px] font-normal text-gray-400">hover for UID / name</span>
      </span>
    )
  }
  // A stacked short header applies to any column that defines one; the
  // ellipsis set additionally gets the "hover cell for ID" affordance.
  if (ELLIPSIS_ONLY_COLUMNS.has(column) || SHORT_HEADERS[column]) {
    const short = SHORT_HEADERS[column] || [column]
    const weight = MEDIUM_WEIGHT_HEADERS.has(column) ? 'font-medium' : 'font-semibold'
    return (
      <span
        className="normal-case tracking-normal"
        title={
          ELLIPSIS_ONLY_COLUMNS.has(column) ? `${column} · hover cell for ID` : column
        }
      >
        <span className={`${weight} uppercase tracking-wide leading-tight`}>{short[0]}</span>
        {short[1] ? (
          <span className="ml-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            {short[1]}
          </span>
        ) : null}
      </span>
    )
  }
  if (SOURCE_USERNAME_COLUMNS.includes(column)) {
    const short = SHORT_HEADERS[column] || [column]
    return (
      <span className="flex min-w-0 flex-col items-start gap-0.5 normal-case tracking-normal">
        <span className="font-semibold uppercase tracking-wide leading-tight">{short[0]}</span>
        {short[1] ? (
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400 leading-tight">
            {short[1]}
          </span>
        ) : null}
        <span className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
          hover for full list
        </span>
      </span>
    )
  }
  if (stackPctChgUnderStreams && isDatedStreamsColumn(column)) {
    const short = shortDatedStreamsHeader(column) || column
    return (
      <span
        className="font-medium uppercase tracking-wide leading-tight normal-case"
        title={`${column} · WoW % shown under streams`}
      >
        <span className="uppercase tracking-wide">{short}</span>
        <span className="ml-0.5 text-[9px] font-normal normal-case tracking-normal text-gray-400">
          %
        </span>
      </span>
    )
  }
  const short = SHORT_HEADERS[column]
  if (short) {
    const weight = MEDIUM_WEIGHT_HEADERS.has(column) ? 'font-medium' : 'font-semibold'
    return (
      <span className="normal-case tracking-normal" title={column}>
        <span className={`block uppercase tracking-wide leading-tight ${weight}`}>{short[0]}</span>
        {short[1] ? (
          <span className="block text-[10px] font-medium uppercase tracking-wide text-gray-400 leading-tight">
            {short[1]}
          </span>
        ) : null}
      </span>
    )
  }
  return <span className="font-medium uppercase tracking-wide">{column}</span>
}

function AnalyzeGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 19V5M8 19V11M12 19V8M16 19V13M20 19V6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function AnalyzeNameCell({ value, identity, column }) {
  const href = analyzePath(identity)
  const label =
    identity?.q ||
    (identity?.id != null ? `mapping #${identity.id}` : null) ||
    identity?.ig_uid ||
    identity?.tt_uid ||
    'artist'
  const ellipsisOnly = ELLIPSIS_ONLY_COLUMNS.has(column)
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <Link
        to={href}
        title={`Analyze ${label}`}
        aria-label={`Analyze ${label}`}
        className="inline-flex shrink-0 rounded p-0.5 text-gray-400 transition hover:bg-[var(--encore-blue-soft)] hover:text-[var(--encore-blue)]"
        onClick={(e) => e.stopPropagation()}
      >
        <AnalyzeGlyph />
      </Link>
      <Link
        to={href}
        title={`Analyze ${label}`}
        className="min-w-0 flex-1 font-medium text-gray-800 hover:text-[var(--encore-blue)] hover:underline"
      >
        <TruncatedText
          value={value}
          ellipsisOnly={ellipsisOnly}
          label={column}
          className="!cursor-pointer !text-inherit"
        />
      </Link>
    </div>
  )
}

/** Operations -> Assets / Creative facet columns (Box inventory presence). */
const BOX_ASSET_FACET_COLUMNS = new Set([
  'Agreements', 'Side Artist', 'Credits', 'Sample', 'Master', 'Atmos',
  'Stems', 'Lyrics', 'Cover Art', 'Canvas', 'Banners', 'Video', 'Photos',
  'Physical',
])

/** Facets shown as a present/missing glyph rather than an "n/m" ratio. */
const BOX_ASSET_PRESENCE_COLUMNS = new Set([
  'Master', 'Atmos', 'Stems', 'Lyrics', 'Cover Art', 'Canvas', 'Banners',
  'Video', 'Photos', 'Physical',
])

function boxFileWebUrl(id) {
  const fid = String(id ?? '').trim()
  return fid ? `https://app.box.com/file/${fid}` : null
}

function boxFolderWebUrl(id) {
  const fid = String(id ?? '').trim()
  return fid ? `https://app.box.com/folder/${fid}` : null
}

/** Rose folder with a red X — the facet folder does not exist in Box. */
function BoxFolderMissingIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Lighter rose folder so the red X reads clearly on top */}
      <path
        d="M3.5 7.5h6.2l1.4 1.6H20.5v9.4c0 .9-.7 1.5-1.5 1.5H5c-.8 0-1.5-.6-1.5-1.5V7.5z"
        fill="#fda4af"
      />
      <path d="M8 10.5h8.5v1.4H8z" fill="#fff1f2" opacity="0.95" />
      <path
        d="M15.2 6.2l4.6 4.6M19.8 6.2l-4.6 4.6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Plain X — the folder exists but holds no file. */
function BoxFileMissingIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 7l10 10M17 7L7 17"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Check — the asset is present. */
function BoxPresentIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5.5 12.5l4.2 4.2L18.5 7.5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Facet cell for Assets / Creative.
 *
 * The crawl precomputes each label ("1/1", "Present", "Folder Missing") and
 * stores the facet's Box folder id + file list in `_facet_meta`, so the cell
 * deep links to app.box.com. Those are plain web URLs built from stored ids —
 * no Box API credential involved.
 *
 * Status reads as a glyph rather than text: folder-missing, file-missing and
 * present each get their own icon; ratio facets keep their "n/m" count.
 */
function BoxAssetStatusCell({ column, value, row }) {
  const raw =
    value == null || String(value).trim() === '' ||
    String(value).trim().toLowerCase() === 'nan'
      ? ''
      : String(value).trim()
  if (!raw) return <span className="text-gray-400">—</span>

  const meta = (row?._facet_meta || {})[column] || {}
  const files = Array.isArray(meta.files) ? meta.files : []
  const folderHref = boxFolderWebUrl(meta.folder_id)
  const primaryHref = files.length ? boxFileWebUrl(files[0].id) : folderHref
  const lower = raw.toLowerCase()

  const fileList = files.length
    ? `\n${files.map((f) => f.name).join('\n')}`
    : ''

  function wrap(node, { tone, title }) {
    const className = `inline-flex items-center justify-center ${tone}`
    if (!primaryHref) {
      return (
        <span className={className} title={title} aria-label={title}>
          {node}
        </span>
      )
    }
    return (
      <a
        href={primaryHref}
        target="_blank"
        rel="noreferrer"
        className={`${className} hover:opacity-80`}
        title={title}
        aria-label={title}
      >
        {node}
      </a>
    )
  }

  if (lower === 'n/a' || lower === 'na' || lower === 'not applicable') {
    return (
      <span className="text-gray-400" title="N/A" aria-label="N/A">
        N/A
      </span>
    )
  }

  if (lower === 'folder missing') {
    // The Side Artist folder is optional — absent means N/A, not an error.
    if (column === 'Side Artist') {
      return (
        <span className="text-gray-400" title="N/A" aria-label="N/A">
          N/A
        </span>
      )
    }
    return wrap(<BoxFolderMissingIcon />, {
      tone: 'text-red-600',
      title: 'Folder Missing',
    })
  }

  if (
    lower === 'file missing' ||
    lower === 'empty folder' ||
    lower === 'agreement missing'
  ) {
    return wrap(<BoxFileMissingIcon />, {
      tone: 'text-red-600',
      title: `File Missing${folderHref ? ' · open folder in Box' : ''}`,
    })
  }

  if (BOX_ASSET_PRESENCE_COLUMNS.has(column) && /\bpresent\b/i.test(raw)) {
    return wrap(<BoxPresentIcon />, {
      tone: 'text-emerald-600',
      title: files.length
        ? `${raw}${fileList}`
        : folderHref
          ? `${raw} · open folder in Box`
          : raw,
    })
  }

  const ratio = raw.match(/^(\d+)\s*\/\s*(\d+)$/)
  if (ratio) {
    const present = Number(ratio[1])
    const tone = present > 0 ? 'text-emerald-700' : 'text-red-600'
    return wrap(<span className="tabular-nums font-medium">{raw}</span>, {
      tone,
      title: `${column}: ${raw}${fileList}`,
    })
  }

  return wrap(<span>{raw}</span>, {
    tone: 'text-gray-700',
    title: `${column}: ${raw}${fileList}`,
  })
}

function renderStaticCell(
  column,
  value,
  percentColumns,
  row,
  enablePeakHighlight,
  linkLabel,
  truncateColumns = [],
  enableEngagementHighlight = false,
  analyzeColumn = null,
  analyzeRequireArtistMapping = false,
  stackPctChgUnderStreams = false,
  onUsernameExpand = null,
) {
  const analyzeOpts = { requireArtistMapping: analyzeRequireArtistMapping }
  if (
    analyzeColumn &&
    column === analyzeColumn &&
    analyzeIdentityFromRow(row, analyzeOpts)
  ) {
    return (
      <AnalyzeNameCell
        value={value}
        identity={analyzeIdentityFromRow(row, analyzeOpts)}
        column={column}
      />
    )
  }

  if (column === 'Link' && value) {
    return (
      <a
        href={String(value)}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-[var(--encore-blue)] hover:underline"
      >
        {linkLabel || 'Open'}
      </a>
    )
  }

  if (column === 'TikTok Sound ID') {
    const id = formatId(value)
    if (id === '—') {
      return <span className="text-gray-400">—</span>
    }
    return (
      <a
        href={`https://www.tiktok.com/music/-${encodeURIComponent(id)}`}
        target="_blank"
        rel="noreferrer"
        className="tabular-nums font-medium text-[var(--encore-blue)] hover:underline"
        title="Open on TikTok"
      >
        {id}
      </a>
    )
  }

  // PGP: Genius ids link out. The artist row carries a canonical Genius URL
  // when the pipeline resolved one; otherwise fall back to the numeric id path.
  if (column === 'Genius Artist ID') {
    const id = formatId(value)
    if (id === '—') {
      return <span className="text-gray-400">—</span>
    }
    const rowUrl = row?.['Genius URL']
    const href =
      rowUrl && String(rowUrl).trim().startsWith('http')
        ? String(rowUrl).trim()
        : `https://genius.com/artists/${encodeURIComponent(id)}`
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="tabular-nums font-medium text-[var(--encore-blue)] hover:underline"
        title="Open artist on Genius"
      >
        {id}
      </a>
    )
  }

  if (column === 'Genius Song ID') {
    const id = formatId(value)
    if (id === '—') {
      return <span className="text-gray-400">—</span>
    }
    return (
      <a
        href={`https://genius.com/songs/${encodeURIComponent(id)}`}
        target="_blank"
        rel="noreferrer"
        className="tabular-nums font-medium text-[var(--encore-blue)] hover:underline"
        title="Open song on Genius"
      >
        {id}
      </a>
    )
  }

  if (BOX_ASSET_FACET_COLUMNS.has(column) && row?._facet_meta !== undefined) {
    return <BoxAssetStatusCell column={column} value={value} row={row} />
  }

  if (column === 'New Follows') {
    return <NewFollowsCell value={value} />
  }

  if (column === 'Recent Count' || column === 'Prior Count') {
    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-400">—</span>
    }
    return <span className="tabular-nums text-gray-800">{formatNumber(value)}</span>
  }

  if (isResidualColumn(column)) {
    const formatted = formatResidual(value)
    if (formatted === '—') {
      return <span className="text-gray-400">—</span>
    }
    return <span className="tabular-nums text-gray-800">{formatted}</span>
  }

  if (
    stackPctChgUnderStreams &&
    isDatedStreamsColumn(column)
  ) {
    const pctCol = pctChgColumnForStreamsWeek(column)
    return (
      <StreamsWithPctChgCell
        streamsValue={value}
        pctValue={pctCol ? row?.[pctCol] : null}
        pctColumn={pctCol}
      />
    )
  }

  if (
    ELLIPSIS_ONLY_COLUMNS.has(column) ||
    column === 'IG Full Name' ||
    column === 'Artist' ||
    truncateColumns.includes(column)
  ) {
    const cellValue = isIdColumn(column) ? formatId(value) : value
    const emphasis = discoveryEmphasisClass(column, row)
    return (
      <TruncatedText
        value={cellValue === '—' ? '' : cellValue}
        ellipsisOnly={ELLIPSIS_ONLY_COLUMNS.has(column)}
        label={column}
        className={emphasis}
      />
    )
  }

  if (column === NEW_FOLLOWS_USERNAMES_COL || column === ALL_FOLLOWS_USERNAMES_COL) {
    return <SourceUsernamesCell value={value} />
  }

  const uidCols = USERNAME_UID_COLUMN[column]
  if (uidCols) {
    const candidates = Array.isArray(uidCols) ? uidCols : [uidCols]
    let uid = null
    for (const col of candidates) {
      const v = row?.[col]
      if (v != null && String(v).trim() !== '' && String(v).trim().toLowerCase() !== 'nan') {
        uid = v
        break
      }
    }
    const node = (
      <UsernameWithUid
        value={value}
        uid={uid}
        verified={isVerifiedValue(row?.Verified)}
      />
    )
    if (typeof onUsernameExpand === 'function' && column === 'IG Username') {
      return (
        <button
          type="button"
          onClick={onUsernameExpand}
          title="Show CORE followers"
          className="max-w-full text-left transition hover:opacity-80"
        >
          {node}
        </button>
      )
    }
    return node
  }

  const engTone = enableEngagementHighlight
    ? engagementHighlightTone(row, column)
    : null

  if (isPercentColumn(column, percentColumns)) {
    return (
      <PercentPill
        value={value}
        formatted={formatCell(column, value, percentColumns)}
        highlightTone={engTone}
        signedTone={!isLevelPercentColumn(column)}
      />
    )
  }

  if (engTone) {
    return <MetricNumber value={value} highlightTone={engTone} />
  }

  const lower = column.toLowerCase()
  if (lower.includes('growth') && !lower.includes('%')) {
    const tone = enablePeakHighlight ? peakHighlightTone(row, column) : null
    return <GrowthNumber value={value} peakTone={tone} />
  }

  // Views / count engagement metrics without a tone still need tabular formatting
  if (
    lower.includes('views') &&
    (lower.includes('30d') || lower.includes('median') || lower.includes('average'))
  ) {
    return <MetricNumber value={value} />
  }

  const emphasis = discoveryEmphasisClass(column, row)
  const formatted = formatCell(column, value, percentColumns)
  if (emphasis) {
    return <span className={emphasis}>{formatted}</span>
  }
  return formatted
}

function rowKey(row, index, uidColumn) {
  if (row?.id != null && String(row.id).trim() !== '' && String(row.id).toLowerCase() !== 'nan') {
    return `id:${String(row.id).trim().replace(/\.0$/, '')}`
  }
  const uid = uidColumn ? row[uidColumn] : null
  return uid != null && uid !== '' ? String(uid) : `row-${index}`
}

function columnCellClass(
  column,
  truncateColumns,
  layout,
  { stackPctChgUnderStreams = false, wrapColumns = [], weighted = false } = {},
) {
  if (layout === 'snoop') {
    if (COUNT_COLUMNS.has(column)) {
      return 'px-2 py-3 text-center tabular-nums'
    }
    if (column === 'Link') {
      return 'px-3 py-3 text-center'
    }
    if (column === 'IG Username') {
      return 'px-4 py-3 text-gray-800'
    }
    if (column === 'IG Full Name') {
      return 'min-w-0 px-3 py-3 text-gray-800'
    }
    if (column === NEW_FOLLOWS_USERNAMES_COL || column === ALL_FOLLOWS_USERNAMES_COL) {
      return 'min-w-0 overflow-hidden px-3 py-3 text-gray-800'
    }
    if (column === 'Status' || column === 'Artist Status') {
      return 'px-3 py-2.5'
    }
    return 'px-3 py-3 text-gray-800'
  }

  if (weighted) {
    // A colgroup already sets this column's width, so no w-0/max-w clamps that
    // would fight it. px-3 keeps pill dropdowns off the next column's edge.
    if (wrapColumns.includes(column)) {
      return 'px-3 py-3 align-top text-gray-800 whitespace-pre-wrap break-words'
    }
    if (column === 'Status' || column === 'Artist Status') {
      return 'px-3 py-2.5 text-center'
    }
    return 'overflow-hidden px-3 py-3 text-gray-800'
  }
  if (COUNT_COLUMNS.has(column)) {
    return 'w-0 max-w-[3.5rem] px-1.5 py-3 text-center'
  }
  if (column === 'Link') {
    return 'w-0 max-w-[2.5rem] px-2 py-3 text-center'
  }
  if (column === 'Verified') {
    return 'w-0 max-w-[3.75rem] px-2 py-3 text-xs text-gray-600'
  }
  if (column === 'Status' || column === 'Artist Status') {
    return 'w-0 min-w-[10rem] max-w-[14rem] px-2 py-2.5'
  }
  if (
    column === 'Artist' ||
    column === 'Instagram' ||
    column === 'TikTok' ||
    column === 'IG Username'
  ) {
    return 'w-0 max-w-[9.5rem] px-3 py-3 text-gray-800'
  }
  if (ELLIPSIS_ONLY_COLUMNS.has(column) || column === 'Luminate Song ID') {
    return 'w-0 max-w-[2.75rem] px-2 py-3 text-center text-gray-800'
  }
  if (column === 'YT Search Topic ID') {
    return 'w-0 max-w-[7.5rem] px-2 py-3 text-gray-800'
  }
  if (wrapColumns.includes(column)) {
    // Wrap instead of nowrap so the column can use its allotted width
    // (PGP Agenda Notes) rather than forcing one long line.
    return 'px-3 py-3 align-top text-gray-800 whitespace-pre-wrap break-words'
  }
  if (truncateColumns.includes(column) || column === 'IG Full Name') {
    return 'w-0 max-w-[11rem] px-3 py-3 text-gray-800'
  }
  if (stackPctChgUnderStreams && isDatedStreamsColumn(column)) {
    return 'whitespace-nowrap px-2 py-1.5 text-right text-gray-800'
  }
  return 'whitespace-nowrap px-3 py-3 text-gray-800'
}

/** Relative widths for the snoop table; normalized to 100% over the visible set. */
const SNOOP_COL_WEIGHTS = {
  'Recent Count': 7,
  'New Follows': 7,
  'IG Username': 13,
  'IG Full Name': 13,
  Link: 4,
  Status: 15,
  'Artist Status': 15,
  'New Follows Usernames': 14,
  'All Follows Usernames': 14,
}

/**
 * Percentage widths keyed by column, rescaled so the visible columns always sum
 * to 100%. Under `table-fixed` a shortfall is handed to whichever column the
 * browser prefers, which surfaced as a large gap before the first column once
 * the merged status control removed the Artist Status column.
 */
function snoopColWidths(visibleColumns) {
  return weightedColWidths(visibleColumns, SNOOP_COL_WEIGHTS)
}

/** Shared by the snoop layout and any page passing its own `columnWeights`. */
function weightedColWidths(visibleColumns, weights) {
  const widths = new Map()
  if (!weights) return widths
  const total = visibleColumns.reduce((sum, column) => sum + (weights[column] || 0), 0)
  if (!total) return widths
  for (const column of visibleColumns) {
    const weight = weights[column]
    if (weight) widths.set(column, `${((weight / total) * 100).toFixed(3)}%`)
  }
  return widths
}

/** Stable key + confirm label for mapping-row trash actions. */
function deleteRowIdentity(row, fallbackIndex) {
  const text = row?.Text != null ? String(row.Text).trim() : ''
  if (text) {
    return { key: `text:${text}`, label: text }
  }
  const entityKey =
    row?.['Entity Key'] != null ? String(row['Entity Key']).trim() : ''
  if (entityKey) {
    const entityType = String(row?.['Entity Type'] || '').trim()
    return {
      key: `ek:${entityType}|${entityKey}`,
      label: `${entityType} ${entityKey}`.trim(),
    }
  }
  const igUid = String(row?.['IG User ID'] ?? '')
    .trim()
    .replace(/\.0$/, '')
  const igUser = String(row?.['IG Username'] ?? '')
    .trim()
    .replace(/^@/, '')
  if (igUid) {
    return {
      key: `iguid:${igUid}`,
      label: igUser ? `@${igUser}` : `IG UID ${igUid}`,
    }
  }
  if (igUser) {
    return { key: `iguser:${igUser.toLowerCase()}`, label: `@${igUser}` }
  }
  if (fallbackIndex != null) {
    return { key: `idx:${fallbackIndex}`, label: 'this row' }
  }
  return {
    key: `row:${JSON.stringify(row).slice(0, 80)}`,
    label: 'this row',
  }
}

/**
 * editableStatus: {
 *   [columnName]: {
 *     target: 'artist_mapping' | 'snoop_mapping',
 *     platform?: 'ig' | 'tt',
 *     uidColumn: string,
 *     rowIdColumn?: string, // e.g. 'id' — prefer PK for Strong-ID duplicate edits
 *     usernameColumn?: string,
 *     options?: Array<string | { value: string, label: string }>,
 *     getOptions?: (row) => Array<string | { value: string, label: string }>,
 *       // per-row option list; overrides options when provided
 *     shouldRemoveRow?: (status, ctx) => boolean,
 *     rowsToRemove?: (status, ctx) => (row) => boolean,
 *       // ctx.rows is the current table when removal is applied (post-optimistic Status)
 *     removeAfterSave?: boolean, // if true, drop row only after onSave succeeds
 *     // Luminate: when true (or target==='luminate'), status/cascade waits for onSave
 *     deferStatusUntilSave?: boolean,
 *     timestampColumn?: string, // e.g. 'Updated At' — bump on successful status save
 *     allowBlank?: boolean, // show "—" blank option (default true); false for snoop_mapping
 *     getValue?: (row) => string, // displayed value when it is derived from several columns
 *     optimisticPatch?: (status) => object, // row patch to paint; defaults to { [column]: status }
 *   }
 * }
 */
export default function DataTable({
  rows: initialRows = [],
  columns = [],
  percentColumns = [],
  defaultSort = null,
  searchPlaceholder = 'Search…',
  searchActions = null,
  editableStatus = null,
  editablePlatforms = null,
  /** Pencil control for pub_mappings free-text/money fields: { onSave(payload) }. */
  editablePubMapping = null,
  onDeleteRow = null,
  snapshotDate = null,
  onStatusError = null,
  hiddenColumns = [],
  truncateColumns = ['IG Full Name'],
  enablePeakHighlight = false,
  enableEngagementHighlight = false,
  legend = null,
  layout = 'unsigned',
  linkLabel = 'IG',
  /** Fired when snoop-source filter has any selection (load 0-new-follow rows when scope=all). */
  onSnoopSourcesAndMultiChange = null,
  /** Snapshot-wide All Follows usernames (incl. New Follows = 0); preferred for filter options. */
  snoopSourceOptions = null,
  /** Parent fetch in flight — suppress empty-state flash while rows clear. */
  loading = false,
  /** Luminate-style NEC filter (hide rows with non-ASCII in title/artist/name). */
  enableHideNonEnglish = false,
  /** When enableHideNonEnglish is true, start with Hide non-English checked. */
  defaultHideNonEnglish = true,
  /**
   * Controlled Hide non-English (optional). When a boolean is passed, parent owns state.
   * Pair with onHideNonEnglishChange; use showHideNonEnglishToggle={false} to render the
   * checkbox elsewhere (e.g. Luminate Artists discovery toggles).
   */
  hideNonEnglish: hideNonEnglishProp = undefined,
  onHideNonEnglishChange = null,
  showHideNonEnglishToggle = true,
  hideNonEnglishColumns = null,
  enableAnalyzeLink = false,
  /** When true, Analyze icon only for rows with artist_mappings (id or Artist Status). */
  analyzeRequireArtistMapping = false,
  /** Luminate: multi-select Genre starts-with filter next to search. */
  enableGenreStartsWithFilter = false,
  genreColumn = 'Luminate Genres',
  /** Luminate Artists: multi-select Country of Origin filter. */
  enableCountryFilter = false,
  countryColumn = 'Country of Origin',
  /** Multi-select Status filter (unsigned / mapping-style tables). */
  enableStatusFilter = false,
  statusColumn = 'Status',
  /** Preferred Status options listed first when present in data. */
  statusFilterPreferred = null,
  /** Roster|Distro scope chips (All / Roster / Distro). */
  enableRosterScopeFilter = false,
  /** Notify parent of active client filters for compact subtitles. */
  onActiveFiltersChange = null,
  /**
   * Freeze thead while scrolling the table body (height-bounded scrollport).
   * Set false for rare pages that need document-level vertical scroll only.
   */
  stickyHeader = true,
  /**
   * Opt-in row expand → lazy history detail.
   * historyFetcher(row) => Promise<payload>
   * Default UI expects { series: [{ date, streams }] } for Luminate charts.
   * Pass renderExpandedHistory(payload) for custom panels (e.g. Snoop CORE followers).
   */
  /**
   * Expand a parent row to reveal child rows held on the row itself
   * (Assets / Creative albums -> tracks). Rendered with the same columns.
   */
  expandableNestedRows = false,
  nestedRowsKey = '_tracks',
  nestedExpandTitle = 'Show tracks',
  nestedRowClassName = 'bg-sky-50/90 text-slate-700',
  /**
   * Second-level expand on each nested child (Assets track -> song credits).
   * `renderNestedDetail(child)` supplies the panel; `nestedDetailKey` names the
   * child field holding its stable id.
   */
  renderNestedDetail = null,
  nestedDetailKey = '_release_folder_id',
  nestedDetailTitle = 'Show credits',
  /** Per-row action buttons, rendered in a trailing column. */
  rowActions = null,
  expandableHistory = false,
  historyFetcher = null,
  renderExpandedHistory = null,
  historyExpandTitle = 'Show history',
  historyLoadingLabel = 'Loading history…',
  /**
   * Artists multiweek: hide separate ``% Chg date → date`` columns and show WoW %
   * under each dated ``Streams YYYY-MM-DD`` cell (compact two-line).
   */
  stackPctChgUnderStreams = false,
  /** Mapping tabs: debounce search to parent so API runs q= server-side. */
  serverSearch = false,
  onServerQueryChange = null,
  truncated = false,
  totalMatched = null,
  /** When true and on the last client page, Next fetches/appends more rows. */
  canLoadMore = false,
  onNeedMoreRows = null,
  loadingMore = false,
  /** Per-page header renames: { [columnName]: 'Short Label' }. */
  headerLabels = null,
  /**
   * Larger type / padding for screen sharing (PGP Agenda "Present mode").
   * Purely presentational — no effect on data, sorting or filters.
   */
  presentMode = false,
  /** With presentMode, the viewport is wide enough to widen columns / go full-bleed. */
  presentExpand = false,
  /**
   * Relative column widths, `{ [columnName]: weight }`, rescaled so the visible
   * columns sum to 100% (PGP Agenda gives Notes the most room). Columns with no
   * weight size to content.
   */
  columnWeights = null,
  /** Columns that wrap onto multiple lines instead of nowrap/truncate (PGP Notes). */
  wrapColumns = [],
  /**
   * Grey ("mute") a row when this hidden column is truthy — e.g. Catalog
   * taken-down products, which stay listed but read as inactive.
   */
  muteRowKey = null,
  /**
   * Tint a row when this column is truthy ('Yes'), e.g. PGP Charts
   * "New This Week". The column is usually hidden from display.
   */
  rowHighlightColumn = null,
  /** Tailwind classes for the rowHighlightColumn tint. */
  rowHighlightClass = 'bg-violet-50',
  /**
   * Comparator for the Status column, replacing the default unsigned-status
   * ordering (PGP Agenda sorts by pipeline stage). `(a, b) => number`.
   */
  statusCompare = null,
  /**
   * Column list to render a skeleton from before the first payload arrives, so
   * the table does not jump from empty to full width.
   */
  placeholderColumns = null,
}) {
  const [query, setQuery] = useState('')
  const [isSearchPending, startSearchTransition] = useTransition()
  const onSearchQueryChange = useCallback((next) => {
    startSearchTransition(() => setQuery(next))
  }, [])
  const [sort, setSort] = useState(() => normalizeSortSpecs(defaultSort))
  const [rows, setRows] = useState(initialRows)
  const [savingKeys, setSavingKeys] = useState({})
  /** While a status save is in flight, show the chosen option (not the old row value). */
  const [pendingStatusByKey, setPendingStatusByKey] = useState({})
  const [deletingKeys, setDeletingKeys] = useState({})
  const [rowErrors, setRowErrors] = useState({})
  /** Parent row keys whose nested children are currently expanded. */
  const [nestedOpenKeys, setNestedOpenKeys] = useState(() => new Set())
  /** Child row ids whose detail panel (song credits) is open. */
  const [nestedDetailKeys, setNestedDetailKeys] = useState(() => new Set())
  const [allSourceFilter, setAllSourceFilter] = useState([])
  /**
   * Row tint flag (PGP Charts "New This Week"). Truthy strings only — the
   * column ships as 'Yes' / '' so a blank must not tint.
   */
  const rowHighlighted = useCallback(
    (row) => {
      if (!rowHighlightColumn) return false
      const raw = row?.[rowHighlightColumn]
      if (raw === true) return true
      const text = raw == null ? '' : String(raw).trim().toLowerCase()
      return text !== '' && text !== 'no' && text !== 'false' && text !== '0'
    },
    [rowHighlightColumn],
  )
  const [allSourceMatchMode, setAllSourceMatchMode] = useState('or')
  /** new = New Follows Usernames; all = All Follows Usernames (quiet weeks). */
  const [allSourceFollowScope, setAllSourceFollowScope] = useState('all')
  const [genreStartsWithFilter, setGenreStartsWithFilter] = useState([])
  const [countryFilter, setCountryFilter] = useState([])
  const [statusFilter, setStatusFilter] = useState([])
  const [rosterScope, setRosterScope] = useState('all') // all | roster | distro
  const [hideNonEnglishUncontrolled, setHideNonEnglishUncontrolled] = useState(
    () => Boolean(enableHideNonEnglish && defaultHideNonEnglish),
  )
  const hideNonEnglishControlled = typeof hideNonEnglishProp === 'boolean'
  const hideNonEnglish = hideNonEnglishControlled
    ? hideNonEnglishProp
    : hideNonEnglishUncontrolled
  const setHideNonEnglish = (next) => {
    const value = typeof next === 'function' ? next(hideNonEnglish) : next
    if (hideNonEnglishControlled) {
      onHideNonEnglishChange?.(value)
    } else {
      setHideNonEnglishUncontrolled(value)
    }
  }
  // Persist across local status edits / soft row updates — only clamp, don't reset to 0.
  const [page, setPage] = useState(1)
  const [expandedKeys, setExpandedKeys] = useState(() => new Set())
  const [historyByKey, setHistoryByKey] = useState({})
  const [historyLoadingKeys, setHistoryLoadingKeys] = useState({})
  const [historyErrorKeys, setHistoryErrorKeys] = useState({})
  /** After progressive load-more, advance one client page once new rows land. */
  const pendingAdvanceRef = useRef(false)
  /** Table body scrollport — used to freeze scroll when status edits re-sort rows. */
  const tableScrollRef = useRef(null)
  const pendingScrollRestoreRef = useRef(null)
  const prevSafePageRef = useRef(null)

  function captureTableScroll() {
    const el = tableScrollRef.current
    pendingScrollRestoreRef.current = {
      tableTop: el?.scrollTop ?? 0,
      tableLeft: el?.scrollLeft ?? 0,
      windowX: typeof window !== 'undefined' ? window.scrollX : 0,
      windowY: typeof window !== 'undefined' ? window.scrollY : 0,
    }
  }

  function restoreTableScroll() {
    const pending = pendingScrollRestoreRef.current
    if (!pending) return
    const el = tableScrollRef.current
    if (el) {
      el.scrollTop = pending.tableTop
      el.scrollLeft = pending.tableLeft
    }
    if (typeof window !== 'undefined') {
      window.scrollTo(pending.windowX, pending.windowY)
    }
  }

  /** After pagination, snap the table viewport to the first rows of the new page. */
  function scrollTableToTop() {
    const el = tableScrollRef.current
    if (!el) return
    el.scrollTop = 0
    // Bring the table card into view (pagination sits below a tall scrollport).
    const card = el.parentElement
    if (card && typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ block: 'start', behavior: 'auto' })
    }
  }

  /** setRows that keeps the user's scroll position (status sort must not jump to top). */
  function setRowsPreservingScroll(updater) {
    captureTableScroll()
    setRows(updater)
  }

  useLayoutEffect(() => {
    if (!pendingScrollRestoreRef.current) return
    restoreTableScroll()
    // Focused <select> can scroll-into-view after layout; re-apply once more.
    const pending = pendingScrollRestoreRef.current
    const id = requestAnimationFrame(() => {
      if (pendingScrollRestoreRef.current === pending) {
        restoreTableScroll()
        pendingScrollRestoreRef.current = null
      }
    })
    return () => cancelAnimationFrame(id)
  })

  useEffect(() => {
    setRows(initialRows)
    setRowErrors({})
    setPendingStatusByKey({})
    setExpandedKeys(new Set())
    setHistoryByKey({})
    setHistoryLoadingKeys({})
    setHistoryErrorKeys({})
    if (pendingAdvanceRef.current) {
      pendingAdvanceRef.current = false
      const nextCount = Math.max(1, Math.ceil((initialRows?.length || 0) / PAGE_SIZE) || 1)
      setPage((p) => Math.min(p + 1, nextCount))
    }
  }, [initialRows])

  const analyzeColumn = useMemo(
    () => (enableAnalyzeLink ? analyzeLinkColumn(columns) : null),
    [enableAnalyzeLink, columns],
  )
  const hasAllFollowsUsernames = useMemo(
    () => columns.includes(ALL_FOLLOWS_USERNAMES_COL),
    [columns],
  )

  const allSourceOptions = useMemo(() => {
    if (Array.isArray(snoopSourceOptions) && snoopSourceOptions.length) {
      const seen = new Map()
      for (const raw of snoopSourceOptions) {
        const name = String(raw ?? '')
          .trim()
          .replace(/^@/, '')
        if (!name || name.toLowerCase() === 'nan' || name.toLowerCase() === 'none') continue
        const key = name.toLowerCase()
        if (!seen.has(key)) seen.set(key, name)
      }
      return [...seen.values()].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      )
    }
    return hasAllFollowsUsernames
      ? collectSourceUsernameOptions(rows, ALL_FOLLOWS_USERNAMES_COL)
      : []
  }, [snoopSourceOptions, hasAllFollowsUsernames, rows])

  // Drop selections that disappeared after a data refresh
  useEffect(() => {
    setAllSourceFilter((prev) => {
      if (!prev.length) return prev
      const allowed = new Set(allSourceOptions.map((n) => n.toLowerCase()))
      const next = prev.filter((n) => allowed.has(n.toLowerCase()))
      return next.length === prev.length ? prev : next
    })
  }, [allSourceOptions])

  // Include New Follows ≤ 0 when filtering All follows, or when searching.
  // New-follows scope only needs rows that have those sources in New Follows Usernames
  // (zeros/negatives won't match that column — skip the heavy min=0 refetch).
  useEffect(() => {
    if (!onSnoopSourcesAndMultiChange) return undefined
    const selected = layout === 'snoop' ? allSourceFilter : []
    const active = selected.length > 0
    const followScope = allSourceFollowScope === 'new' ? 'new' : 'all'
    const applyServerAnd =
      active && allSourceMatchMode === 'and' && selected.length >= 2
    onSnoopSourcesAndMultiChange({
      active,
      selected: active ? selected : [],
      applyServerAnd,
      matchMode: allSourceMatchMode,
      followScope,
    })
    return undefined
  }, [
    layout,
    allSourceFilter,
    allSourceMatchMode,
    allSourceFollowScope,
    onSnoopSourcesAndMultiChange,
  ])

  // Debounced server-side search (full-table q=) instead of only filtering the
  // already-loaded page. Reported regardless of `serverSearch` because the parent may
  // use the query to decide whether to search server-side at all (snoop: min length).
  useEffect(() => {
    if (typeof onServerQueryChange !== 'function') return undefined
    const handle = window.setTimeout(() => {
      onServerQueryChange(query.trim())
    }, 300)
    return () => window.clearTimeout(handle)
  }, [query, onServerQueryChange])

  const visibleColumns = useMemo(() => {
    const hidden = new Set([
      ...HIDDEN_UID_COLUMNS,
      ...(layout === 'snoop' ? HIDDEN_SNOOP_COLUMNS : []),
      ...hiddenColumns,
    ])
    const base = columns.filter((col) => {
      if (hidden.has(col)) return false
      if (stackPctChgUnderStreams && isMultiweekPctChgColumn(col)) return false
      return true
    })
    if (editablePubMapping) {
      if (base.includes(PUB_MAPPING_EDIT_COLUMN)) return base
      // Pencil sits first so it stays put as deal columns come and go.
      return [PUB_MAPPING_EDIT_COLUMN, ...base]
    }
    if (!editablePlatforms) return base
    const statusIdx = base.indexOf('Status')
    const insertAt = statusIdx >= 0 ? statusIdx + 1 : 0
    if (base.includes(MAPPING_EDIT_COLUMN)) return base
    return [...base.slice(0, insertAt), MAPPING_EDIT_COLUMN, ...base.slice(insertAt)]
  }, [
    columns,
    hiddenColumns,
    layout,
    editablePlatforms,
    editablePubMapping,
    stackPctChgUnderStreams,
  ])

  const snoopWidths = useMemo(() => {
    if (columnWeights) return weightedColWidths(visibleColumns, columnWeights)
    return layout === 'snoop' ? snoopColWidths(visibleColumns) : new Map()
  }, [layout, visibleColumns, columnWeights])

  const necColumns = useMemo(
    () => (hideNonEnglishColumns?.length ? hideNonEnglishColumns : DEFAULT_NEC_COLUMNS),
    [hideNonEnglishColumns],
  )

  const genreOptions = useMemo(() => {
    if (!enableGenreStartsWithFilter) return []
    const col = columns.includes(genreColumn) ? genreColumn : null
    if (!col) return []
    return genreStartsWithOptionsFromRows(rows, col)
  }, [enableGenreStartsWithFilter, genreColumn, columns, rows])

  const countryOptions = useMemo(() => {
    if (!enableCountryFilter) return []
    const col = columns.includes(countryColumn) ? countryColumn : null
    if (!col) return []
    return distinctColumnOptionsFromRows(rows, col)
  }, [enableCountryFilter, countryColumn, columns, rows])

  const statusOptions = useMemo(() => {
    if (!enableStatusFilter) return []
    const col = columns.includes(statusColumn) ? statusColumn : null
    if (!col) return []
    const fromRows = distinctColumnOptionsFromRows(rows, col)
    const preferred = Array.isArray(statusFilterPreferred)
      ? statusFilterPreferred.map((s) => String(s).trim()).filter(Boolean)
      : []
    if (!preferred.length) return fromRows
    const set = new Set(fromRows.map((s) => s.toLowerCase()))
    const head = preferred.filter((s) => set.has(s.toLowerCase()))
    const headLower = new Set(head.map((s) => s.toLowerCase()))
    const rest = fromRows.filter((s) => !headLower.has(s.toLowerCase()))
    return [...head, ...rest]
  }, [enableStatusFilter, statusColumn, statusFilterPreferred, columns, rows])

  useEffect(() => {
    if (typeof onActiveFiltersChange !== 'function') return undefined
    onActiveFiltersChange({
      query: query.trim().length > 0,
      statusFilter,
      rosterScope,
      genreFilter: genreStartsWithFilter,
      countryFilter,
    })
  }, [
    onActiveFiltersChange,
    query,
    statusFilter,
    rosterScope,
    genreStartsWithFilter,
    countryFilter,
  ])

  // One lowercase haystack per row so each keystroke is O(rows), not O(rows × cols).
  const rowSearchHaystacks = useMemo(() => {
    return rows.map((row) =>
      columns.map((col) => String(row[col] ?? '').toLowerCase()).join('\u0000'),
    )
  }, [rows, columns])

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase()
    let indices = rows.map((_, i) => i)

    indices = indices.filter((i) =>
      rowMatchesSourceFilter(
        rows[i],
        allSourceFollowScope === 'new'
          ? NEW_FOLLOWS_USERNAMES_COL
          : ALL_FOLLOWS_USERNAMES_COL,
        allSourceFilter,
        allSourceMatchMode,
      ),
    )

    if (enableGenreStartsWithFilter && genreStartsWithFilter.length) {
      indices = indices.filter((i) =>
        rowMatchesGenreStartsWith(rows[i], genreColumn, genreStartsWithFilter),
      )
    }

    if (enableCountryFilter && countryFilter.length) {
      indices = indices.filter((i) =>
        rowMatchesExactColumn(rows[i], countryColumn, countryFilter),
      )
    }

    if (enableStatusFilter && statusFilter.length) {
      indices = indices.filter((i) =>
        rowMatchesExactColumn(rows[i], statusColumn, statusFilter),
      )
    }

    if (enableRosterScopeFilter && rosterScope !== 'all') {
      const want = rosterScope === 'roster' ? 'roster' : 'distro'
      indices = indices.filter((i) => {
        const s = String(rows[i]?.Status ?? '')
          .trim()
          .toLowerCase()
        return s === want
      })
    }

    if (enableHideNonEnglish && hideNonEnglish) {
      indices = indices.filter((i) => !rowHasNonEnglish(rows[i], necColumns))
    }

    if (q && !serverSearch) {
      // Search visible + hidden UID columns so UID lookup still works
      indices = indices.filter((i) => rowSearchHaystacks[i].includes(q))
    }

    let next = indices.map((i) => rows[i])
    const specs = normalizeSortSpecs(sort)
    if (!specs.length) return next
    return [...next].sort((left, right) => {
      for (const spec of specs) {
        const { column, direction = 'asc' } = spec
        // Derived status controls (merged Snoop status) sort on the rendered value,
        // not the backing column, which is blank for every row they render.
        const getValue = editableStatus?.[column]?.getValue
        const derive = typeof getValue === 'function' ? getValue : (r) => r[column]
        const a = derive(left)
        const b = derive(right)
        // Per-page Status comparator (PGP: sort by pipeline stage, not A-Z).
        const cmp =
          statusCompare && (column === 'Status' || column === 'Artist Status')
            ? compareSortable(a, b, direction, statusCompare)
            : column === 'Status' || column === 'Artist Status'
              ? compareSortable(a, b, direction, compareUnsignedStatus)
              : column === 'Updated At' || column === 'Created At'
                ? compareSortable(a, b, direction, compareTimestamps)
                : SHORT_DATE_COLUMNS.has(column)
                  ? compareSortable(a, b, direction, compareShortDates)
                  : compareSortable(a, b, direction, compareValues)
        if (cmp !== 0) return cmp
      }
      return 0
    })
  }, [
    rows,
    rowSearchHaystacks,
    query,
    serverSearch,
    sort,
    editableStatus,
    statusCompare,
    allSourceFilter,
    allSourceMatchMode,
    allSourceFollowScope,
    enableGenreStartsWithFilter,
    genreStartsWithFilter,
    genreColumn,
    enableCountryFilter,
    countryFilter,
    countryColumn,
    enableStatusFilter,
    statusFilter,
    statusColumn,
    enableRosterScopeFilter,
    rosterScope,
    enableHideNonEnglish,
    hideNonEnglish,
    necColumns,
  ])

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE) || 1)
  const safePage = Math.min(Math.max(1, page), pageCount)
  const pageStart = (safePage - 1) * PAGE_SIZE
  const pageRows = useMemo(
    () => filteredRows.slice(pageStart, pageStart + PAGE_SIZE),
    [filteredRows, pageStart],
  )

  // Clamp page when filters shrink the result set; never jump to 1 on status edits alone.
  useEffect(() => {
    if (page !== safePage) setPage(safePage)
  }, [page, safePage])

  // Snap table to top when the page index changes (Prev/Next or filter reset).
  useLayoutEffect(() => {
    if (prevSafePageRef.current == null) {
      prevSafePageRef.current = safePage
      return
    }
    if (prevSafePageRef.current === safePage) return
    prevSafePageRef.current = safePage
    // Don't fight status-edit scroll restore.
    if (pendingScrollRestoreRef.current) return
    scrollTableToTop()
  }, [safePage])

  // Reset to page 1 only when search / NEC / source / sort change (not on row edits).
  useEffect(() => {
    setPage(1)
  }, [
    query,
    hideNonEnglish,
    allSourceFilter,
    allSourceMatchMode,
    allSourceFollowScope,
    genreStartsWithFilter,
    countryFilter,
    statusFilter,
    rosterScope,
    sort,
  ])

  function toggleSort(column) {
    if (column === MAPPING_EDIT_COLUMN || column === PUB_MAPPING_EDIT_COLUMN) return
    setSort((prev) => {
      const specs = normalizeSortSpecs(prev)
      const idx = specs.findIndex((s) => s.column === column)
      if (idx === 0) {
        const next = [...specs]
        next[0] = {
          column,
          direction: specs[0].direction === 'desc' ? 'asc' : 'desc',
        }
        return next
      }
      if (idx > 0) {
        // Promote existing secondary to primary (keep its direction).
        const next = [...specs]
        const [spec] = next.splice(idx, 1)
        return [spec, ...next].slice(0, MAX_SORT_SPECS)
      }
      // New primary; previous primary becomes secondary.
      return [{ column, direction: 'desc' }, ...specs].slice(0, MAX_SORT_SPECS)
    })
  }

  /** Pencil save for pub_mappings free-text / money fields. */
  async function handlePubMappingSave(row, payload) {
    if (!editablePubMapping?.onSave) return
    const pubId = row?.['Pub Mapping ID']
    if (pubId == null || String(pubId).trim() === '') {
      const msg = 'Missing Pub Mapping ID'
      onStatusError?.(msg)
      return
    }
    const key = `pubmapping:${pubId}`

    // Paint the new values immediately; revert the row on failure.
    const previous = { ...row }
    setRows((prevRows) =>
      prevRows.map((r) =>
        String(r['Pub Mapping ID']) !== String(pubId)
          ? r
          : {
              ...r,
              Name: payload.name,
              'Genius Artist ID': payload.clearGeniusArtistId
                ? null
                : payload.geniusArtistId,
              Lawyer: payload.lawyer,
              Calc: payload.calc,
              'Committed $': payload.committedMoney,
              Uncommitted: payload.uncommitted,
              Option: payload.optionMoney,
              Notes: payload.notes,
            },
      ),
    )
    setSavingKeys((prev) => ({ ...prev, [key]: true }))
    setRowErrors((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })

    try {
      await editablePubMapping.onSave({ mappingId: pubId, ...payload })
    } catch (err) {
      setRows((prevRows) =>
        prevRows.map((r) =>
          String(r['Pub Mapping ID']) === String(pubId) ? previous : r,
        ),
      )
      const msg = err?.message || String(err)
      setRowErrors((prev) => ({ ...prev, [key]: msg }))
      onStatusError?.(msg)
    } finally {
      setSavingKeys((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  async function handlePlatformSave(row, { platform, column, uidColumn, username, previousUsername }) {
    if (!editablePlatforms?.onSave) return
    const mappingId = row?.id
    if (mappingId == null || String(mappingId).trim() === '') {
      const msg = 'Missing mapping id for platform update'
      onStatusError?.(msg)
      return
    }
    const key = `platform:${mappingId}:${platform}`
    const nextUsername =
      username == null || String(username).trim() === '' ? null : String(username).trim()
    const prev = previousUsername == null ? '' : String(previousUsername).trim()
    const nextNorm = nextUsername ? nextUsername.replace(/^@/, '') : ''
    const prevNorm = prev.replace(/^@/, '')
    const clearUid =
      Boolean(uidColumn) && (!nextNorm || nextNorm.toLowerCase() !== prevNorm.toLowerCase())

    setRows((prevRows) =>
      prevRows.map((r) => {
        if (String(r.id) !== String(mappingId)) return r
        const updated = { ...r, [column]: nextUsername }
        if (clearUid && uidColumn) updated[uidColumn] = null
        return updated
      }),
    )
    setSavingKeys((prevKeys) => ({ ...prevKeys, [key]: true }))
    setRowErrors((prevErrs) => {
      const copy = { ...prevErrs }
      delete copy[key]
      return copy
    })

    const clearSaving = () => {
      setSavingKeys((prevKeys) => {
        if (!prevKeys[key]) return prevKeys
        const copy = { ...prevKeys }
        delete copy[key]
        return copy
      })
    }
    const safetyTimer = window.setTimeout(clearSaving, 45_000)

    try {
      await editablePlatforms.onSave({
        mappingId,
        platform,
        username: nextUsername,
        row,
      })
    } catch (err) {
      setRows((prevRows) =>
        prevRows.map((r) => {
          if (String(r.id) !== String(mappingId)) return r
          const rolled = { ...r, [column]: previousUsername || null }
          if (clearUid && uidColumn) {
            rolled[uidColumn] = row[uidColumn] ?? null
          }
          return rolled
        }),
      )
      const msg = err?.message || 'Platform update failed'
      setRowErrors((prevErrs) => ({ ...prevErrs, [key]: msg }))
      onStatusError?.(msg)
      throw err
    } finally {
      window.clearTimeout(safetyTimer)
      clearSaving()
    }
  }

  async function handleStatusChange(row, column, nextStatus, config) {
    if (config.allowBlank === false && !String(nextStatus ?? '').trim()) {
      return
    }
    const rowIdCol = config.rowIdColumn
    const rowId =
      rowIdCol && row?.[rowIdCol] != null && String(row[rowIdCol]).trim() !== ''
        ? String(row[rowIdCol]).trim().replace(/\.0$/, '')
        : null
    const uid = row[config.uidColumn]
    if (!rowId && (uid == null || String(uid).trim() === '')) {
      const msg = `Missing ${config.uidColumn} for status update`
      setRowErrors((prev) => ({ ...prev, [`${column}:${uid}`]: msg }))
      onStatusError?.(msg)
      return
    }
    const key = statusSaveKey(column, uid, rowId)
    const isLuminate = config.target === 'luminate'
    // Luminate: wait for onSave before mutating Status / dropping (Snoop still
    // optimistically paints Status on the clicked row; drop waits via removeAfterSave).
    const deferUntilSave = Boolean(config.deferStatusUntilSave) || isLuminate
    const optimisticStatus = isLuminate
      ? luminateStoredStatus(nextStatus) || nextStatus
      : nextStatus
    // Derived controls (e.g. the merged Snoop status) paint a different column than
    // the one they render in, so let the config own the patch and its revert.
    const optimisticPatch =
      typeof config.optimisticPatch === 'function'
        ? config.optimisticPatch(optimisticStatus)
        : { [column]: optimisticStatus || null }
    const previousPatch = Object.fromEntries(
      Object.keys(optimisticPatch).map((col) => [col, row[col] ?? null]),
    )
    const tsCol = config.timestampColumn
    const previousTs = tsCol ? row?.[tsCol] : undefined
    const touchedAt = tsCol ? new Date().toISOString() : null

    if (!deferUntilSave) {
      setRowsPreservingScroll((prev) =>
        prev.map((r) => {
          const rId =
            rowIdCol && r?.[rowIdCol] != null && String(r[rowIdCol]).trim() !== ''
              ? String(r[rowIdCol]).trim().replace(/\.0$/, '')
              : null
          if (statusSaveKey(column, r[config.uidColumn], rId) !== key) return r
          const next = { ...r, ...optimisticPatch }
          if (tsCol && touchedAt) next[tsCol] = touchedAt
          return next
        }),
      )
    }
    setPendingStatusByKey((prev) => ({ ...prev, [key]: nextStatus }))
    setSavingKeys((prev) => ({ ...prev, [key]: true }))
    setRowErrors((prev) => {
      const copy = { ...prev }
      delete copy[key]
      return copy
    })

    const clearSaving = () => {
      setSavingKeys((prev) => {
        if (!prev[key]) return prev
        const copy = { ...prev }
        delete copy[key]
        return copy
      })
      setPendingStatusByKey((prev) => {
        if (!(key in prev)) return prev
        const copy = { ...prev }
        delete copy[key]
        return copy
      })
    }
    const safetyTimer = window.setTimeout(clearSaving, 45_000)

    let rowsSnapshot = null
    let alsoUpdate = []

    try {
      if (config.prepareSave) {
        const prep = await config.prepareSave({
          row,
          column,
          status: nextStatus,
          uid: uid != null ? String(uid).trim().replace(/\.0$/, '') : null,
          mappingId: rowId,
          snapshotDate,
        })
        alsoUpdate = prep?.alsoUpdate || []
      }

      const saveCtx = { alsoUpdate, row }
      const willRemove = Boolean(config.shouldRemoveRow?.(nextStatus, saveCtx))
      const removeAfterSave = Boolean(config.removeAfterSave) || isLuminate

      const applyRowRemoval = (prev) => {
        if (typeof config.rowsToRemove === 'function') {
          const removePred = config.rowsToRemove(nextStatus, {
            ...saveCtx,
            rows: prev,
          })
          if (typeof removePred === 'function') {
            return prev.filter((r) => !removePred(r))
          }
        }
        return prev.filter((r) => {
          const rId =
            rowIdCol && r?.[rowIdCol] != null && String(r[rowIdCol]).trim() !== ''
              ? String(r[rowIdCol]).trim().replace(/\.0$/, '')
              : null
          return statusSaveKey(column, r[config.uidColumn], rId) !== key
        })
      }

      // Prefer post-save removal (Snoop / Luminate). Pre-save only for legacy configs.
      if (willRemove && !removeAfterSave) {
        setRowsPreservingScroll((prev) => {
          rowsSnapshot = prev
          return applyRowRemoval(prev)
        })
      }

      let saveResult
      if (config.onSave) {
        saveResult = await config.onSave({
          row,
          column,
          status: nextStatus,
          uid: uid != null ? String(uid).trim().replace(/\.0$/, '') : null,
          mappingId: rowId,
          snapshotDate,
          alsoUpdate,
        })
        if (Array.isArray(saveResult?.alsoUpdate)) {
          alsoUpdate = saveResult.alsoUpdate
        }
      }

      if (willRemove && removeAfterSave) {
        setRowsPreservingScroll((prev) => applyRowRemoval(prev))
      } else if (!willRemove && isLuminate) {
        // Fan-out Artist/Label (and Track) status onto all matching rows after success.
        const cascadeCols = config.luminateCascadeColumns || {}
        setRowsPreservingScroll((prev) =>
          applyLuminateStatusCascade(prev, {
            sourceRow: row,
            nextStatus,
            alsoUpdate,
            artistCol: cascadeCols.artistCol || 'Primary Artist',
            imprintCol: cascadeCols.imprintCol || 'Imprint',
            songIdCol:
              cascadeCols.songIdCol === undefined
                ? config.uidColumn || 'Luminate Song ID'
                : cascadeCols.songIdCol,
            statusCol: column,
          }),
        )
      } else if (!willRemove && !deferUntilSave && alsoUpdate?.length) {
        setRowsPreservingScroll((prev) =>
          prev.map((r) => {
            const rId =
              rowIdCol && r?.[rowIdCol] != null && String(r[rowIdCol]).trim() !== ''
                ? String(r[rowIdCol]).trim().replace(/\.0$/, '')
                : null
            if (statusSaveKey(column, r[config.uidColumn], rId) !== key) return r
            const next = {
              ...r,
              [column]: luminateStoredStatus(nextStatus) || nextStatus || null,
            }
            for (const companion of alsoUpdate) {
              const stored = luminateStoredStatus(companion) || companion
              if (String(companion).includes('Track')) next['Track Status'] = stored
              if (String(companion).includes('Artist')) next['Primary Artist Status'] = stored
              if (String(companion).includes('Label')) next['Imprint Status'] = stored
            }
            return next
          }),
        )
      } else if (!willRemove && deferUntilSave && !isLuminate) {
        // Non-luminate defer: update the clicked row only after save.
        setRowsPreservingScroll((prev) =>
          prev.map((r) => {
            const rId =
              rowIdCol && r?.[rowIdCol] != null && String(r[rowIdCol]).trim() !== ''
                ? String(r[rowIdCol]).trim().replace(/\.0$/, '')
                : null
            if (statusSaveKey(column, r[config.uidColumn], rId) !== key) return r
            const next = { ...r, ...optimisticPatch }
            if (tsCol && touchedAt) next[tsCol] = touchedAt
            return next
          }),
        )
      }
    } catch (error) {
      if (rowsSnapshot) {
        setRowsPreservingScroll(rowsSnapshot)
      } else if (!deferUntilSave) {
        setRowsPreservingScroll((prev) =>
          prev.map((r) => {
            const rId =
              rowIdCol && r?.[rowIdCol] != null && String(r[rowIdCol]).trim() !== ''
                ? String(r[rowIdCol]).trim().replace(/\.0$/, '')
                : null
            if (statusSaveKey(column, r[config.uidColumn], rId) !== key) return r
            const next = { ...r, ...previousPatch }
            if (tsCol) next[tsCol] = previousTs ?? null
            return next
          }),
        )
      }
      const msg = error.message || 'Status update failed'
      if (!/cancelled/i.test(msg)) {
        setRowErrors((prev) => ({ ...prev, [key]: msg }))
        onStatusError?.(msg)
      }
    } finally {
      window.clearTimeout(safetyTimer)
      clearSaving()
    }
  }

  async function handleDeleteRow(row) {
    if (!onDeleteRow) return
    const { key, label } = deleteRowIdentity(row)
    if (!window.confirm(`Remove ${label} from mapping?`)) return

    setDeletingKeys((prev) => ({ ...prev, [key]: true }))
    setRowErrors((prev) => {
      const copy = { ...prev }
      delete copy[key]
      return copy
    })
    try {
      await onDeleteRow(row)
      setRows((prev) => prev.filter((r) => r !== row))
    } catch (error) {
      const msg = error.message || 'Delete failed'
      setRowErrors((prev) => ({ ...prev, [key]: msg }))
      onStatusError?.(msg)
    } finally {
      setDeletingKeys((prev) => {
        const copy = { ...prev }
        delete copy[key]
        return copy
      })
    }
  }

  function toggleNestedDetail(key) {
    setNestedDetailKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function toggleNested(key) {
    setNestedOpenKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function toggleHistoryExpand(row, index) {
    if (!expandableHistory || typeof historyFetcher !== 'function') return
    const key = rowKey(
      row,
      pageStart + index,
      editableStatus ? Object.values(editableStatus)[0]?.uidColumn : null,
    )
    const willExpand = !expandedKeys.has(key)
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (willExpand) next.add(key)
      else next.delete(key)
      return next
    })
    if (!willExpand) return
    if (historyByKey[key] || historyLoadingKeys[key]) return
    setHistoryLoadingKeys((prev) => ({ ...prev, [key]: true }))
    setHistoryErrorKeys((prev) => {
      const copy = { ...prev }
      delete copy[key]
      return copy
    })
    try {
      const payload = await historyFetcher(row)
      setHistoryByKey((prev) => ({ ...prev, [key]: payload ?? {} }))
    } catch (err) {
      setHistoryErrorKeys((prev) => ({
        ...prev,
        [key]: err?.message || 'Failed to load history',
      }))
    } finally {
      setHistoryLoadingKeys((prev) => {
        const copy = { ...prev }
        delete copy[key]
        return copy
      })
    }
  }

  if (!visibleColumns.length) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            className={[
              'flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center',
              // Pages with their own toolbar (PGP Charts chips) need the full
              // width; the 5xl cap clipped them.
              searchActions ? '' : 'sm:max-w-5xl',
            ].join(' ')}
          >
            <TableSearchInput
              placeholder={searchPlaceholder}
              onQueryChange={onSearchQueryChange}
            />
            {searchActions}
          </div>
        </div>
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
          {loading ? 'Loading…' : 'No rows found'}
        </div>
      </div>
    )
  }

  const sortSpecs = normalizeSortSpecs(sort)
  const multiSort = sortSpecs.length > 1

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div
          className={[
            'flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center',
            // Pages with their own toolbar (PGP Charts chips) need the full
            // width; the 5xl cap clipped them.
            searchActions ? '' : 'sm:max-w-5xl',
          ].join(' ')}
        >
          <TableSearchInput
            placeholder={searchPlaceholder}
            onQueryChange={onSearchQueryChange}
          />
          {searchActions}
          {enableRosterScopeFilter ? (
            <div
              className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm shadow-sm"
              role="group"
              aria-label="Roster scope"
            >
              {[
                { id: 'all', label: 'All' },
                { id: 'roster', label: 'Roster' },
                { id: 'distro', label: 'Distro' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setRosterScope(id)}
                  className={[
                    'rounded-md px-2.5 py-1.5 font-medium transition',
                    rosterScope === id
                      ? 'bg-emerald-50 text-emerald-800'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  ].join(' ')}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
          {enableStatusFilter && statusOptions.length ? (
            <MultiSelectFilter
              options={statusOptions}
              selected={statusFilter}
              onChange={setStatusFilter}
              emptyLabel="Filter by status…"
              selectedSingularPrefix="Status"
              footerNoun="statuses"
              searchPlaceholder="Search status…"
            />
          ) : null}
          {enableGenreStartsWithFilter ? (
            <MultiSelectFilter
              options={genreOptions}
              selected={genreStartsWithFilter}
              onChange={setGenreStartsWithFilter}
              emptyLabel="Genre starts with…"
              selectedSingularPrefix="Genre"
              footerNoun="genres"
              searchPlaceholder="Search genres…"
            />
          ) : null}
          {enableCountryFilter ? (
            <MultiSelectFilter
              options={countryOptions}
              selected={countryFilter}
              onChange={setCountryFilter}
              emptyLabel="Country…"
              selectedSingularPrefix="Country"
              footerNoun="countries"
              searchPlaceholder="Search countries…"
            />
          ) : null}
          {enableHideNonEnglish && showHideNonEnglishToggle ? (
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm hover:border-gray-300">
              <input
                type="checkbox"
                checked={hideNonEnglish}
                onChange={(event) => setHideNonEnglish(event.target.checked)}
                className="rounded border-gray-300 text-[var(--encore-blue)] focus:ring-blue-100"
              />
              Hide non-English
            </label>
          ) : null}
          {hasAllFollowsUsernames || allSourceOptions.length ? (
            <MultiSelectFilter
              options={allSourceOptions}
              selected={allSourceFilter}
              onChange={setAllSourceFilter}
              matchMode={allSourceMatchMode}
              onMatchModeChange={setAllSourceMatchMode}
              followScope={allSourceFollowScope}
              onFollowScopeChange={setAllSourceFollowScope}
              emptyLabel="Filter by snoop source…"
              selectedSingularPrefix="Snoop"
              footerNoun="snoop sources"
              searchPlaceholder="Search sources…"
            />
          ) : null}
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <p className="text-sm text-gray-500">
            Showing{' '}
            <span className="font-medium text-gray-800">
              {filteredRows.length
                ? `${pageStart + 1}–${Math.min(pageStart + PAGE_SIZE, filteredRows.length)}`
                : '0'}
            </span>{' '}
            of <span className="font-medium text-gray-800">{filteredRows.length}</span>
            {rows.length !== filteredRows.length ? (
              <>
                {' '}
                (filtered from <span className="font-medium text-gray-800">{rows.length}</span>)
              </>
            ) : null}
            {truncated && totalMatched != null ? (
              <>
                {' '}
                · <span className="font-medium text-amber-800">{totalMatched}</span> matched
                (loaded {rows.length}
                {canLoadMore ? '; Next page loads more' : ''}
                ; search covers the full filtered set)
              </>
            ) : null}
          </p>
          {legend}
        </div>
      </div>

      <div
        className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-opacity ${
          isSearchPending ? 'opacity-70' : 'opacity-100'
        }`}
      >
        <div
          ref={tableScrollRef}
          className={
            stickyHeader
              ? 'max-h-[calc(100vh-14rem)] overflow-auto'
              : 'overflow-x-auto'
          }
        >
          <table
            className={[
              'w-full border-collapse text-left',
              // Present mode bumps type for screen sharing (PGP Agenda); on a
              // wide enough viewport it goes a step further.
              presentExpand
                ? 'text-lg [&_td]:py-3.5'
                : presentMode
                  ? 'text-base [&_td]:py-3'
                  : 'text-sm',
              layout === 'snoop'
                ? 'table-fixed min-w-[1200px]'
                : columnWeights
                  ? // Explicit weights need table-fixed to be honoured.
                    `table-fixed ${presentExpand ? 'min-w-full' : 'min-w-[1100px]'}`
                  : presentExpand
                    ? // Full-bleed: let columns use the window, not a fixed floor.
                      'min-w-full'
                    : 'min-w-[1100px]',
            ].join(' ')}
          >
            {layout === 'snoop' || columnWeights ? (
              <colgroup>
                {expandableHistory || expandableNestedRows ? (
                  <col style={{ width: '2.5rem' }} />
                ) : null}
                {onDeleteRow ? <col style={{ width: '2.5rem' }} /> : null}
                {visibleColumns.map((column) => (
                  <col key={column} style={{ width: snoopWidths.get(column) }} />
                ))}
                {rowActions ? <col style={{ width: '3rem' }} /> : null}
              </colgroup>
            ) : null}
            <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
              <tr>
                {expandableHistory || expandableNestedRows ? (
                  <th className="w-0 border-b border-gray-200 bg-gray-50 px-2 py-2.5 text-xs font-medium text-gray-400">
                    <span className="sr-only">
                      {expandableNestedRows ? 'Expand tracks' : 'Expand history'}
                    </span>
                  </th>
                ) : null}
                {onDeleteRow ? (
                  <th className="w-0 border-b border-gray-200 bg-gray-50 px-2 py-2.5 text-xs font-medium text-gray-400">
                    <span className="sr-only">Delete</span>
                  </th>
                ) : null}
                {visibleColumns.map((column) => {
                  if (column === PUB_MAPPING_EDIT_COLUMN) {
                    return (
                      <th
                        key={column}
                        className="w-0 border-b border-gray-200 px-1.5 py-2.5 text-xs font-medium text-gray-400"
                        title="Edit name / Genius id / deal fields"
                      >
                        <span className="sr-only">Edit mapping</span>
                      </th>
                    )
                  }
                  if (column === MAPPING_EDIT_COLUMN) {
                    return (
                      <th
                        key={column}
                        className="w-0 border-b border-gray-200 px-1.5 py-2.5 text-xs font-medium text-gray-400"
                        title="Edit platform usernames"
                      >
                        <span className="sr-only">Edit platforms</span>
                      </th>
                    )
                  }
                  const isStatus = column === 'Status' || column === 'Artist Status'
                  const isCount = COUNT_COLUMNS.has(column)
                  const isLink = column === 'Link'
                  const isVerified = column === 'Verified'
                  const isStackedStreams =
                    stackPctChgUnderStreams && isDatedStreamsColumn(column)
                  const isNameCol =
                    column === 'Artist' ||
                    column === 'Instagram' ||
                    column === 'TikTok' ||
                    column === 'IG Username' ||
                    column === 'IG Full Name'
                  const isYtTopic = column === 'YT Search Topic ID'
                  // Breathing room between Total Follows → New Follows, New Follows → Username, Link → Status
                  const padAfterTotal = layout === 'snoop' && column === 'New Follows'
                  const padAfterCounts = layout === 'snoop' && column === 'IG Username'
                  const padBeforeStatus = layout === 'snoop' && column === 'Status'
                  const sortIdx = sortSpecs.findIndex((s) => s.column === column)
                  const sortActive = sortIdx >= 0
                  const sortRank = sortActive ? sortIdx + 1 : null
                  const sortDirection = sortActive ? sortSpecs[sortIdx].direction : undefined
                  const sortTitle = sortActive
                    ? multiSort
                      ? `Sort ${sortRank === 1 ? 'primary' : `secondary (${sortRank})`} — click another column to stack (max ${MAX_SORT_SPECS})`
                      : 'Click to flip direction; click another column for secondary sort'
                    : 'Click to sort; click another column for secondary sort'
                  return (
                  <th
                    key={column}
                    className={[
                      'border-b border-gray-200 py-2.5 text-xs font-medium text-gray-400',
                      layout === 'snoop'
                        ? [
                            isCount ? 'px-2 text-center' : '',
                            isLink ? 'px-3 text-center' : '',
                            padAfterTotal ? 'pl-5 pr-2' : '',
                            padAfterCounts ? 'pl-6 pr-3' : '',
                            padBeforeStatus ? 'pl-6 pr-3' : '',
                            !isCount && !isLink && !padAfterTotal && !padAfterCounts && !padBeforeStatus
                              ? 'px-3'
                              : '',
                          ].join(' ')
                        : [
                            isCount ? 'w-0 max-w-[3.5rem] px-1.5 text-center' : '',
                            isLink ? 'w-0 max-w-[2.5rem] px-2 text-center' : '',
                            isVerified ? 'w-0 max-w-[3.75rem] px-2' : '',
                            isStatus ? 'w-0 min-w-[10rem] max-w-[14rem] px-2' : '',
                            isNameCol ? 'w-0 max-w-[9.5rem] px-3' : '',
                            isYtTopic ? 'w-0 max-w-[7.5rem] px-2' : '',
                            isStackedStreams ? 'px-2 text-right' : '',
                            !isStatus &&
                            !isNameCol &&
                            !isCount &&
                            !isLink &&
                            !isVerified &&
                            !isYtTopic &&
                            !isStackedStreams
                              ? 'px-3'
                              : '',
                          ].join(' '),
                    ].join(' ')}
                    title={isYtTopic ? 'YT Search Topic ID' : sortTitle}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(column)}
                      title={sortTitle}
                      className={[
                        'inline-flex max-w-full items-center gap-1 text-left hover:text-gray-700',
                        isCount || isLink ? 'justify-center' : '',
                        isStackedStreams ? 'ml-auto justify-end text-right' : '',
                      ].join(' ')}
                    >
                      <span className="min-w-0">
                        {columnHeaderLabel(column, { stackPctChgUnderStreams, headerLabels })}
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1">
                        {editableStatus?.[column] ? (
                          <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-blue-600">
                            edit
                          </span>
                        ) : null}
                        <SortIndicator
                          active={sortActive}
                          direction={sortDirection}
                          rank={sortRank}
                          multi={multiSort}
                        />
                      </span>
                    </button>
                  </th>
                  )
                })}
                {rowActions ? (
                  <th className="w-0 border-b border-gray-200 bg-gray-50 px-2 py-2.5 text-xs font-medium text-gray-400">
                    <span className="sr-only">Row actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, index) => {
                const rk = rowKey(
                  row,
                  pageStart + index,
                  editableStatus ? Object.values(editableStatus)[0]?.uidColumn : null,
                )
                const isExpanded = expandableHistory && expandedKeys.has(rk)
                const nestedChildren =
                  expandableNestedRows && Array.isArray(row?.[nestedRowsKey])
                    ? row[nestedRowsKey]
                    : []
                const isNestedOpen = nestedChildren.length > 0 && nestedOpenKeys.has(rk)
                const colSpan =
                  visibleColumns.length +
                  (onDeleteRow ? 1 : 0) +
                  (expandableHistory || expandableNestedRows ? 1 : 0)
                return (
                  <Fragment key={rk}>
                <tr
                  className={[
                    'border-b border-gray-100 hover:bg-blue-50/40',
                    rowHighlighted(row)
                      ? rowHighlightClass
                      : (pageStart + index) % 2 === 1
                        ? 'bg-gray-50/70'
                        : 'bg-white',
                    muteRowKey && isTruthyFlag(row?.[muteRowKey])
                      ? 'text-gray-400 opacity-60'
                      : '',
                  ].join(' ')}
                >
                  {expandableNestedRows ? (
                    <td className="w-0 px-2 py-2 align-middle">
                      {nestedChildren.length ? (
                        <button
                          type="button"
                          title={
                            isNestedOpen
                              ? 'Hide tracks'
                              : `${nestedExpandTitle} (${nestedChildren.length})`
                          }
                          aria-expanded={isNestedOpen}
                          onClick={() => toggleNested(rk)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition hover:bg-blue-50 hover:text-[var(--encore-blue)]"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M9 6l6 6-6 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className={isNestedOpen ? 'rotate-90 origin-center transition' : 'transition'}
                            />
                          </svg>
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                  {expandableHistory ? (
                    <td className="w-0 px-2 py-2 align-middle">
                      <button
                        type="button"
                        title={isExpanded ? 'Hide detail' : historyExpandTitle}
                        aria-expanded={isExpanded}
                        onClick={() => toggleHistoryExpand(row, index)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition hover:bg-blue-50 hover:text-[var(--encore-blue)]"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                          className={isExpanded ? 'rotate-90 transition' : 'transition'}
                        >
                          <path
                            d="M9 6l6 6-6 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </td>
                  ) : null}
                  {onDeleteRow ? (
                    <td className="w-0 px-2 py-2 align-middle">
                      {(() => {
                        const { key: delKey } = deleteRowIdentity(row, pageStart + index)
                        const busy = Boolean(deletingKeys[delKey])
                        return (
                          <button
                            type="button"
                            title="Remove from mapping"
                            disabled={busy}
                            onClick={() => handleDeleteRow(row)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                          >
                            {busy ? (
                              <span className="h-3.5 w-3.5 animate-pulse rounded-full bg-rose-300" />
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path
                                  d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </button>
                        )
                      })()}
                    </td>
                  ) : null}
                  {visibleColumns.map((column) => {
                    if (column === PUB_MAPPING_EDIT_COLUMN) {
                      const pubId = row?.['Pub Mapping ID']
                      const saveKey = `pubmapping:${pubId}`
                      return (
                        <td key={column} className="w-0 px-1.5 py-2 align-middle">
                          <PubMappingEdit
                            row={row}
                            saving={Boolean(savingKeys[saveKey])}
                            error={rowErrors[saveKey] || null}
                            onSave={(payload) => handlePubMappingSave(row, payload)}
                          />
                        </td>
                      )
                    }
                    if (column === MAPPING_EDIT_COLUMN) {
                      const mapId = row?.id
                      const saveKeyPrefix = `platform:${mapId}:`
                      const saving = Object.keys(savingKeys).some((k) => k.startsWith(saveKeyPrefix))
                      const errKey = Object.keys(rowErrors).find((k) => k.startsWith(saveKeyPrefix))
                      return (
                        <td key={column} className="w-0 px-1.5 py-2 align-middle">
                          <MappingPlatformEdit
                            row={row}
                            saving={saving}
                            error={errKey ? rowErrors[errKey] : null}
                            onSave={(payload) => handlePlatformSave(row, payload)}
                          />
                        </td>
                      )
                    }
                    const config = editableStatus?.[column]
                    if (config) {
                      const uid = row[config.uidColumn]
                      const rowId =
                        config.rowIdColumn &&
                        row?.[config.rowIdColumn] != null &&
                        String(row[config.rowIdColumn]).trim() !== ''
                          ? String(row[config.rowIdColumn]).trim().replace(/\.0$/, '')
                          : null
                      const saveKey = statusSaveKey(column, uid, rowId)
                      return (
                        <td
                          key={column}
                          className={[
                            columnCellClass(column, truncateColumns, layout, {
                              wrapColumns,
                              weighted: Boolean(columnWeights),
                              stackPctChgUnderStreams,
                            }),
                            layout === 'snoop' && column === 'New Follows' ? 'pl-5' : '',
                            layout === 'snoop' && column === 'IG Username' ? 'pl-6' : '',
                            layout === 'snoop' && column === 'Status' ? 'pl-6' : '',
                          ].join(' ')}
                        >
                          <div className="flex min-w-0 flex-col gap-1">
                            {config.control === 'checkbox' ? (
                              <BoolCheckbox
                                checked={(() => {
                                  const pending = pendingStatusByKey[saveKey]
                                  const raw =
                                    pending !== undefined
                                      ? pending
                                      : typeof config.getValue === 'function'
                                        ? config.getValue(row)
                                        : row[column]
                                  return isTruthyFlag(raw)
                                })()}
                                saving={Boolean(savingKeys[saveKey])}
                                title={config.checkboxTitle}
                                onChange={(next) =>
                                  handleStatusChange(row, column, next ? 'Yes' : '', config)
                                }
                              />
                            ) : (
                            <StatusSelect
                              value={
                                pendingStatusByKey[saveKey] !== undefined
                                  ? pendingStatusByKey[saveKey]
                                  : typeof config.getValue === 'function'
                                    ? config.getValue(row)
                                    : row[column]
                              }
                              options={
                                // getOptions lets a row vary its own list (PGP
                                // Charts only offers False Credit on single-credit rows).
                                (typeof config.getOptions === 'function'
                                  ? config.getOptions(row)
                                  : config.options) || ARTIST_STATUS_OPTIONS
                              }
                              allowBlank={config.allowBlank !== false}
                              saving={Boolean(savingKeys[saveKey])}
                              onChange={(next) => handleStatusChange(row, column, next, config)}
                              wide={layout === 'snoop' || config.target === 'luminate'}
                              centerText={Boolean(config.centerText)}
                            />
                            )}
                            {rowErrors[saveKey] ? (
                              <span className="max-w-full truncate text-[10px] text-rose-600" title={rowErrors[saveKey]}>
                                {rowErrors[saveKey]}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      )
                    }
                    return (
                      <td
                        key={column}
                        className={[
                          columnCellClass(column, truncateColumns, layout, {
                            wrapColumns,
                            weighted: Boolean(columnWeights),
                            stackPctChgUnderStreams,
                          }),
                          layout === 'snoop' && column === 'New Follows' ? 'pl-5' : '',
                          layout === 'snoop' && column === 'IG Username' ? 'pl-6' : '',
                          layout === 'snoop' && column === 'Status' ? 'pl-6' : '',
                        ].join(' ')}
                      >
                        {renderStaticCell(
                          column,
                          row[column],
                          percentColumns,
                          row,
                          enablePeakHighlight,
                          linkLabel,
                          truncateColumns,
                          enableEngagementHighlight,
                          analyzeColumn,
                          analyzeRequireArtistMapping,
                          stackPctChgUnderStreams,
                          expandableHistory && column === 'IG Username'
                            ? () => toggleHistoryExpand(row, index)
                            : null,
                        )}
                      </td>
                    )
                  })}
                  {rowActions ? (
                    <td className="w-0 px-2 py-2 align-middle">
                      {rowActions(row)}
                    </td>
                  ) : null}
                </tr>
                {isExpanded ? (
                  <tr key={`${rk}-history`} className="border-b border-gray-100 bg-slate-50/80">
                    <td colSpan={colSpan} className="px-4 py-3">
                      {historyLoadingKeys[rk] ? (
                        <p className="text-sm text-gray-500">{historyLoadingLabel}</p>
                      ) : historyErrorKeys[rk] ? (
                        <p className="text-sm text-rose-600">{historyErrorKeys[rk]}</p>
                      ) : typeof renderExpandedHistory === 'function' ? (
                        renderExpandedHistory(historyByKey[rk] || {}, row)
                      ) : (
                        <div className="max-w-3xl">
                          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                            Weekly streams
                          </p>
                          <StreamsHistoryChart
                            series={
                              Array.isArray(historyByKey[rk]?.series)
                                ? historyByKey[rk].series
                                : Array.isArray(historyByKey[rk])
                                  ? historyByKey[rk]
                                  : []
                            }
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ) : null}
                {isNestedOpen
                  ? nestedChildren.flatMap((child, childIdx) => {
                      const childId = String(child?.[nestedDetailKey] ?? `${rk}-${childIdx}`)
                      const detailOpen =
                        Boolean(renderNestedDetail) && nestedDetailKeys.has(childId)
                      return [
                      <tr
                        key={`${rk}-nested-${childIdx}`}
                        className={`border-b border-gray-100 ${nestedRowClassName}`}
                      >
                        <td className="w-0 px-2 py-2 align-middle">
                          {renderNestedDetail ? (
                            <button
                              type="button"
                              title={detailOpen ? 'Hide credits' : nestedDetailTitle}
                              aria-expanded={detailOpen}
                              onClick={() => toggleNestedDetail(childId)}
                              className="ml-3 inline-flex h-6 w-6 items-center justify-center rounded text-slate-500 transition hover:bg-white/70 hover:text-[var(--encore-blue)]"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path
                                  d="M9 6l6 6-6 6"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className={detailOpen ? 'rotate-90 origin-center transition' : 'transition'}
                                />
                              </svg>
                            </button>
                          ) : null}
                        </td>
                        {onDeleteRow ? <td className="w-0 px-2 py-2" /> : null}
                        {visibleColumns.map((column) => (
                          <td
                            key={column}
                            className={columnCellClass(column, truncateColumns, layout, {
                              wrapColumns,
                              weighted: Boolean(columnWeights),
                              stackPctChgUnderStreams,
                            })}
                          >
                            {renderStaticCell(
                              column,
                              child[column],
                              percentColumns,
                              child,
                              enablePeakHighlight,
                              linkLabel,
                              truncateColumns,
                              enableEngagementHighlight,
                              analyzeColumn,
                              analyzeRequireArtistMapping,
                              stackPctChgUnderStreams,
                              null,
                            )}
                          </td>
                        ))}
                        {rowActions ? <td className="w-0 px-2 py-2" /> : null}
                      </tr>,
                      detailOpen ? (
                        <tr
                          key={`${rk}-nested-${childIdx}-detail`}
                          className="border-b border-gray-100 bg-slate-50/80"
                        >
                          <td
                            colSpan={
                              visibleColumns.length +
                              1 +
                              (onDeleteRow ? 1 : 0) +
                              (rowActions ? 1 : 0)
                            }
                            className="px-2 py-1"
                          >
                            {renderNestedDetail(child)}
                          </td>
                        </tr>
                      ) : null,
                      ]
                    })
                  : null}
                  </Fragment>
                )
              })}
              {!pageRows.length ? (
                <tr>
                  <td
                    colSpan={
                      visibleColumns.length +
                      (onDeleteRow ? 1 : 0) +
                      (expandableHistory || expandableNestedRows ? 1 : 0) +
                      (rowActions ? 1 : 0)
                    }
                    className="px-4 py-10 text-center text-gray-400"
                  >
                    {loading ? 'Loading…' : 'No rows found'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {filteredRows.length > PAGE_SIZE || canLoadMore ? (
        <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center">
          <p className="text-xs text-gray-500">
            Page <span className="font-medium text-gray-800">{safePage}</span> of{' '}
            <span className="font-medium text-gray-800">{pageCount}</span>
            <span className="text-gray-400"> · {PAGE_SIZE} rows per page</span>
            {canLoadMore ? (
              <span className="text-amber-700"> · Next fetches more rows</span>
            ) : null}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={safePage <= 1 || loadingMore}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={loadingMore || (safePage >= pageCount && !canLoadMore)}
              onClick={async () => {
                if (safePage < pageCount) {
                  setPage((p) => Math.min(pageCount, p + 1))
                  return
                }
                if (!canLoadMore || typeof onNeedMoreRows !== 'function') return
                const ok = await onNeedMoreRows()
                if (ok) pendingAdvanceRef.current = true
              }}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Next'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
